"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { io, type Socket } from "socket.io-client";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  loadTokens,
  type MarketDetail,
  type OrderRecord,
  type OrderbookResponse,
  type PositionRecord,
} from "@/lib/api";
import {
  formatMoney,
  formatMoneyPrimary,
  formatUsdcCentsForDisplay,
  type MoneyCurrency,
} from "@/lib/format";
import { getOutcomeIcon } from "@/lib/outcome-icons";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { useDisplayCurrencyPrimary } from "@/lib/useDisplayCurrencyPrimary";
import { useFxRate } from "@/lib/useFxRate";
import { pickPrimaryCashWallet } from "@/lib/walletPicker";
// STORY-813 PR 5c — order-status compat helpers retired. The matcher
// now writes only the new vocabulary (live / matched / delayed /
// unmatched / cancelled) so callers compare directly.
import { useKycGateRedirect } from "@/lib/useKycGateRedirect";
import { ComposerButton } from "@/components/ComposerButton";
import { useBuilderRefCode } from "@/lib/builderRefCode";
import { OutcomeCard } from "@/components/trade/OutcomeCard";
import { SummaryRow } from "@/components/trade/SummaryRow";

interface IncomingTradeEvent {
  tradeId: string;
  marketSlug: string;
  priceCents: number;
  sizeShares: string;
  outcomeId: string;
  outcomeSortOrder: number;
  platformFeeCents: string;
  createdAt: string;
}

interface TradePanelProps {
  market: MarketDetail;
  // STORY B (multi-outcome trading parity, 2026-05-14) — decouple the
  // panel from the `sortOrder === 0 / 1` binary lookup so the same
  // surface can drive any outcome pair on a multi-outcome market.
  //
  // When `boundOutcome` is provided:
  //   - The YES side resolves to `{ id: yesOutcomeId, currentPriceCents:
  //     yesPriceCents }` instead of `market.outcomes.find(o => sortOrder
  //     === 0)`.
  //   - The NO side resolves to `{ id: noOutcomeId, currentPriceCents:
  //     noPriceCents }` when both are provided. For multi_outcome
  //     markets (where each outcome is independently a YES contract
  //     priced 1-99¢ and there is no real complementary NO contract
  //     — the matcher's `_matchBuyTakerCrossBook` returns early on
  //     >2 outcomes per `apps/api/src/orders/orders.service.ts`),
  //     the caller omits `noOutcomeId` / `noPriceCents` and the
  //     panel renders a 1-card YES-only layout. Same-side BUYs on
  //     that outcome route as usual; there is no synthetic NO trade
  //     because the matcher has nothing to match it against.
  //   - When `label` is set the panel renders a one-line "Trading:
  //     X" banner above the outcome cards so the user always sees
  //     which outcome of a multi-outcome ladder is bound.
  //
  // When `boundOutcome` is undefined the panel falls back to the
  // existing `market.outcomes.find(o => sortOrder === 0 / 1)` lookup
  // (binary markets — no caller change needed).
  boundOutcome?: {
    yesOutcomeId: string;
    yesPriceCents: number;
    noOutcomeId?: string;
    noPriceCents?: number;
    label?: string;
    iconUrl?: string | null;
  };
  // 2026-05-11 — embedding-friendly overrides so the same component
  // serves the market page AND the dashboard "Close position" modal
  // without duplicating ~600 lines of orderbook/balance/fee logic.
  // URL searchParams still take precedence for the market-page deep-
  // link flow; these props are pure fallbacks for the modal context
  // (which can't update the URL without a navigation).
  initialMode?: Mode;
  initialSide?: Outcome;
  initialCloseIntent?: boolean;
  // 2026-05-11 — initialAmount / initialPriceCents added for the
  // dashboard "Edit pending order" flow. Same pattern as the other
  // initial* props: URL searchParams (?amount=, ?price=) still take
  // precedence so market-page deep-links keep working; these props
  // are pure fallbacks for the modal context. Passing both makes
  // the modal open pre-filled with the user's existing pending
  // sell order's size + price — they tweak + submit, the close-
  // modal's existing cancel-and-replace path swaps it in.
  initialAmount?: string;
  initialPriceCents?: number;
  // 2026-05-12 — Edit-pending-sell flow from DashboardTabs. When set,
  // the panel:
  //   1. Excludes this order from `reservedShares` so `availableShares`
  //      reflects the post-cancel state (the user can resize up to
  //      their full holding, not just `holding − pending`).
  //   2. Skips the closeIntent prefill effect's heldShares override so
  //      `initialAmount` (the existing order's size) sticks instead of
  //      getting clobbered with the gross position.
  //   3. On submit, calls `api.cancelOrder(editingOrderId)` BEFORE
  //      `api.placeOrder(...)` so the new order replaces the old one
  //      atomically (from the user's perspective). Without (1) and (2)
  //      the modal showed "30.42 held" while accepting a 77.98-share
  //      Edit input — irreconcilable share math on a money screen.
  editingOrderId?: string;
  // Compact layout: hides the other outcome's "SELL N" card + the
  // buy/sell toggle when the modal is anchored to one specific
  // position. Founder direction: dashboard close-modal should be
  // focused, not a full trade surface.
  compact?: boolean;
  // Fires when the order placed event would otherwise be only
  // surfaced via the in-panel toast — useful when the host (modal)
  // wants to dismiss itself on a successful place.
  onOrderPlaced?: () => void;
}

type Outcome = "yes" | "no";
type Mode = "buy" | "sell";
type OrderType = "limit" | "market";
type AmountMode = "dollars" | "shares";

/**
 * Polymarket-style trade panel (redesigned 2026-04-26).
 *
 * Layout hierarchy: Order type → Mode → big outcome card → amount → CTA.
 * Replaces the prior 4-toggle stack (Buy/Sell + YES/NO + Limit/Market +
 * shares input) with a card-driven flow where the price + side selection
 * are merged into one click.
 *
 * Slippage selector is intentionally NOT shipped — the matcher does not
 * yet honor a user-specified tolerance (uses a fixed 99¢/1¢ envelope on
 * market orders), so a UI selector would misrepresent behavior. Filed
 * as a separate backend story.
 *
 * The card-driven price display uses the last-trade price (liveYesPrice /
 * liveNoPrice) labeled as "implied probability". True best-ask / best-bid
 * would require an extra orderbook query per render; the gap is small
 * with closed-beta liquidity and can be revisited if user feedback shows
 * confusion. TODO when we want exact-fill prices: pull from the
 * orderbook endpoint.
 */
export function TradePanel({
  market,
  boundOutcome,
  initialMode: initialModeProp,
  initialSide: initialSideProp,
  initialCloseIntent: initialCloseIntentProp,
  initialAmount: initialAmountProp,
  initialPriceCents: initialPriceCentsProp,
  editingOrderId,
  compact = false,
  onOrderPlaced,
}: TradePanelProps) {
  const { t } = useTranslation();
  const displayCurrency = useDisplayCurrency();
  // EPIC-029 BR-primary display flip Story 3 (2026-05-08) —
  // displayCurrencyPrimary picks which side leads in dual-currency
  // contexts. region=brazil defaults to 'BRL'; a Story-6 toggle on
  // /settings/display lets the user override per-account. The TradePanel
  // already renders BRL primary when displayCurrency='BRL' via fmtFx;
  // this hook is the formal resolver for follow-up surfaces (Stories 4
  // receipts/history) that need an explicit dual-display via
  // formatMoneyPrimary, and for the in-panel "Total" row's secondary
  // line below.
  const displayCurrencyPrimary = useDisplayCurrencyPrimary();
  // 2026-05-09 — refactored to USDC-as-truth model (per CLAUDE.md
  // "Default settlement currency: USDC. Local fiat shown as a display
  // layer."). The trade panel was previously written against the
  // closed-beta v1 assumption that the matcher worked in BRL cents,
  // and every internal money value was kept in BRL with display-
  // currency conversion happening at fmtFx. After STORY-1700-part-3
  // flipped the ledger + matcher to USDC, the BRL routing made every
  // cost wrong by the USDC↔BRL rate for users with USDC wallets — a
  // $1 input became a R$5 reservation = $5 USDC out (founder report
  // 2026-05-09 "every dollar bet takes 5 dollars from balance").
  //
  // Now: ALL internal money state in this panel is in USDC cents.
  // walletCurrency may be BRL on legacy accounts; we normalize the
  // wallet read to USDC cents on entry. Display conversion happens
  // at one place — fmtFx(usdcCents) — for rendering. Polymarket /
  // Kalshi parity: matcher unit = display unit for US-flag users
  // (1:1 USD/USDC), no FX leg in the math.
  const fxBrl = useFxRate("BRL");
  const usdcPerBrl = fxBrl?.rate ?? null;
  // Convert displayCurrency cents → USDC cents. Identity for USD/USDC
  // (closed-beta convention). For BRL display, divide by the rate
  // (1 BRL = 1/rate USDC at e.g. 5:1 → 0.20 USDC).
  const displayToUsdcRatio =
    displayCurrency === "USD" || displayCurrency === "USDC"
      ? 1
      : displayCurrency === "BRL" && usdcPerBrl !== null && usdcPerBrl > 0
        ? 1 / usdcPerBrl
        : 1;
  // fmtFx(usdcCents): render USDC cents in the user's display currency
  // via the central `formatUsdcCentsForDisplay` helper. Rate-missing
  // fallback (BRL display, no rate yet) keeps USDC so the user sees a
  // real number — `formatUsdcCentsForDisplay` mirrors that behaviour.
  const fmtFx = (usdcCents: number): string =>
    formatUsdcCentsForDisplay(usdcCents, displayCurrency, usdcPerBrl);
  const router = useRouter();
  const searchParams = useSearchParams();
  const kycRedirect = useKycGateRedirect();
  // STORY B (2026-05-14) — when `boundOutcome` is provided we resolve
  // YES/NO from the prop instead of the binary `sortOrder === 0 / 1`
  // lookup. The shape stays minimal — only `id` + `currentPriceCents`
  // are referenced downstream — so we synthesize a `MarketOutcome`-
  // compatible object from the bound prop. For multi_outcome markets
  // the caller omits `noOutcomeId / noPriceCents`; in that case the
  // NO side is synthesized at `100 - YES` and the render path hides
  // the NO card (only the matcher's same-side YES BUY route applies
  // — cross-book pair-mint returns early on >2 outcomes per
  // `apps/api/src/orders/orders.service.ts` _matchBuyTakerCrossBook).
  //
  // useMemo so the object identity stays stable across renders —
  // the WS subscribe effect below carries `yesOutcome` / `noOutcome`
  // in its deps array and we don't want a fresh literal each render
  // triggering reconnects.
  const yesOutcome = useMemo(
    () =>
      boundOutcome
        ? (market.outcomes.find((o) => o.id === boundOutcome.yesOutcomeId) ?? {
            id: boundOutcome.yesOutcomeId,
            sortOrder: 0,
            name: { en: boundOutcome.label ?? "Yes" },
            currentPriceCents: boundOutcome.yesPriceCents,
            volumeCents: "0",
            openInterestShares: "0",
          })
        : market.outcomes.find((o) => o.sortOrder === 0),
    [market.outcomes, boundOutcome],
  );
  const noOutcome = useMemo(
    () =>
      boundOutcome
        ? boundOutcome.noOutcomeId
          ? (market.outcomes.find(
              (o) => o.id === boundOutcome.noOutcomeId,
            ) ?? {
              id: boundOutcome.noOutcomeId,
              sortOrder: 1,
              name: { en: "No" },
              currentPriceCents:
                boundOutcome.noPriceCents ?? 100 - boundOutcome.yesPriceCents,
              volumeCents: "0",
              openInterestShares: "0",
            })
          : {
              // Multi-outcome: no real complementary NO contract on
              // the matcher. Mirror the YES id so the place-order
              // call still targets the bound row when the synthetic
              // NO surface is interacted with — the render path
              // hides the NO card so this is the defensive fallback,
              // not the primary path.
              id: boundOutcome.yesOutcomeId,
              sortOrder: 1,
              name: { en: "No" },
              currentPriceCents:
                boundOutcome.noPriceCents ?? 100 - boundOutcome.yesPriceCents,
              volumeCents: "0",
              openInterestShares: "0",
            }
        : market.outcomes.find((o) => o.sortOrder === 1),
    [market.outcomes, boundOutcome],
  );

  // Deep-link parsing — `?trade=sell&outcome=yes|no` opens the panel
  // pre-set on that side (existing pattern from positions card).
  // `?close=true` additionally opts into one-click position close: the
  // amount field gets pre-filled with the user's full holding (in
  // shares mode) once positions load. Only honored alongside
  // trade=sell since closing a non-existent position makes no sense.
  //
  // Polymarket-style return-from-login flow: when an unauthenticated
  // user clicks Buy YES, we encode the full intent (mode + outcome +
  // order type + amount + amountMode + limit price) into the URL,
  // round-trip to /login?next=<this URL>, and prefill on return. The
  // params below cover that round-trip plus the existing close flow.
  // URL params take precedence for market-page deep-links; prop
  // overrides serve the modal context where the URL stays on /home.
  const initialMode: Mode =
    searchParams.get("trade") === "sell"
      ? "sell"
      : searchParams.get("trade") === "buy"
        ? "buy"
        : (initialModeProp ?? "buy");
  const initialSide: Outcome =
    searchParams.get("outcome") === "no"
      ? "no"
      : searchParams.get("outcome") === "yes"
        ? "yes"
        : (initialSideProp ?? "yes");
  const closeIntent =
    initialMode === "sell" &&
    (searchParams.get("close") === "true" || initialCloseIntentProp === true);
  // STORY-812 phase 2 (2026-05-01) — single Buy/Sell flow per
  // founder direction from the Polymarket comparison study. The
  // Market/Limit toggle is removed: the user always provides a max
  // price, and the matcher smart-routes (FAK if the price crosses
  // the book, GTC otherwise — handled by STORY-810/811 backend).
  // STORY-812 phase 3d (2026-05-02) — orderType is now a const
  // rather than state; the market-mode branches that depended on
  // it have been deleted alongside this change. Kept as a named
  // constant (not inlined) because the placeOrder API contract
  // still requires the field on the request body.
  const orderType: OrderType = "limit";
  // Default amount mode (founder feedback 2026-04-29):
  //   - BUY: always 'dollars' (user has cash, not shares; share-count
  //     would force a mental conversion).
  //   - SELL on a market the user actually holds: 'shares' (selling
  //     the position's natural unit).
  //   - SELL on a market the user doesn't hold: 'dollars' (default;
  //     they'd see a 0-shares input otherwise, which is confusing).
  // The "held position" half can't be decided synchronously because
  // positions load async after mount. We start in `dollars` (the
  // safe default) and a positions-loaded effect below switches SELL
  // → `shares` when a position is detected on the selected outcome
  // — UNLESS a URL param or closeIntent already pinned the choice.
  const amountModeParam = searchParams.get("amountMode");
  const urlPinnedAmountMode =
    amountModeParam === "dollars" ||
    amountModeParam === "shares" ||
    closeIntent;
  const initialAmountMode: AmountMode =
    amountModeParam === "dollars"
      ? "dollars"
      : amountModeParam === "shares" || closeIntent
        ? "shares"
        : "dollars";
  // URL params take precedence (market-page deep-link flow); props
  // are the fallback for the modal context (no URL update).
  const initialAmount = searchParams.get("amount") ?? initialAmountProp ?? "";
  const initialLimitPrice =
    searchParams.get("price") ??
    (initialPriceCentsProp != null ? String(initialPriceCentsProp) : "");

  const [mode, setMode] = useState<Mode>(initialMode);
  const [side, setSide] = useState<Outcome>(initialSide);
  const [amountMode, setAmountMode] = useState<AmountMode>(initialAmountMode);
  const [amountInput, setAmountInput] = useState(initialAmount);
  const [limitPriceInput, setLimitPriceInput] = useState(initialLimitPrice);
  // STORY-709 — slippage tolerance input. Cents from outcome anchor;
  // applies to market orders only (limits already enforce a ceiling
  // via priceCents). Null = "use my user-default from STORY-710"
  // which is fetched async on mount; the input pre-populates from
  // that value but the user can override per-order.
  const [maxSlippageCentsInput, setMaxSlippageCentsInput] =
    useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // STORY-902b bug fix 2026-04-29 — read the live taker fee rate from
  // /v1/platform/fees on mount. The previous hardcoded 200 bps (2%)
  // ignored admin changes via /admin/fees → the displayed fee
  // diverged from what the matcher actually charged. Default to 200
  // so the form renders sane numbers before the fetch resolves.
  const [takerFeeBps, setTakerFeeBps] = useState<number>(200);
  // EPIC-031 STORY-1102 phase 2 — captures `?bc=<hex>` on first hit
  // and persists it to the granbay.bc cookie, then rides on every
  // subsequent placeOrder. Returns null when no code is present
  // (direct visits with no builder attribution).
  const builderRefCode = useBuilderRefCode();
  // `tone` decides the toast's colour. 'success' = green (the order
  // did what the user asked: filled or placed as a resting limit).
  // 'warning' = amber (the order didn't quite do what was asked —
  // auto-converted to limit, or cancelled with no fill in strict
  // mode). Green-on-cancel was misleading: users read it as
  // "filled" because green = success in our tokens.
  const [success, setSuccess] = useState<{
    title: string;
    detail: string;
    tone: "success" | "warning";
    // STORY-812 phase 3c — when the placed order has a resting
    // remainder, surface a "Cancel pending" action right on the
    // success toast. orderId is the row to call cancelOrder on;
    // pendingShares is just for the button label.
    pendingOrderId?: string;
    pendingShares?: number;
  } | null>(null);
  const [positions, setPositions] = useState<PositionRecord[]>([]);
  // Bug-fix 2026-04-28 — held-shares display + sell validation must
  // exclude shares already reserved by the user's open SELL limit
  // orders on this market+outcome. Map: outcomeId → unfilled-shares
  // across the user's open SELL limits, computed in the same load()
  // that fetches positions.
  const [reservedShares, setReservedShares] = useState<Record<string, number>>(
    {},
  );
  const [cashBalanceRawCents, setCashBalanceRawCents] = useState<number | null>(
    null,
  );
  // STORY-A (#1556) — locked_orders wallet balance, in the same raw
  // currency as cashBalanceRawCents. Sum of all open BUY reservations
  // (size × limit + 2% fee headroom). Surfaced in the trade panel as
  // the "reserved" leg of total/available/reserved so the user
  // doesn't think money is missing when their cash wallet shows lower
  // than the header total.
  const [lockedOrdersRawCents, setLockedOrdersRawCents] = useState<
    number | null
  >(null);
  // STORY-A — open BUY orders this user has on this market or any
  // other, populated alongside cash + locked_orders so the
  // "Reserved" disclosure can list them on demand. Each row carries
  // its market slug, side, price, remaining shares, reserved_cents
  // so the disclosure renders without a second API hit.
  const [myReservingOrders, setMyReservingOrders] = useState<
    Array<{
      id: string;
      marketSlug: string;
      side: "buy" | "sell";
      priceCents: number;
      remainingShares: number;
      reservedCents: number;
    }>
  >([]);
  const [reservedDisclosureOpen, setReservedDisclosureOpen] = useState(false);
  // EPIC-029 STORY-1700 part 2 carry-over: cash wallet may be USDC
  // (post-Part-2 signups) or BRL (pre-Part-2 accounts). Track the
  // wallet's actual currency so we can fx-convert to BRL cents — the
  // matcher unit assumed by every reservation/affordability check
  // below — without bricking new users whose wallet is USDC.
  const [walletCurrency, setWalletCurrency] = useState<MoneyCurrency | null>(
    null,
  );
  const [formPulse, setFormPulse] = useState(false);
  // STORY-307d — controls the high-value confirmation modal.
  const [confirming, setConfirming] = useState(false);
  // EPIC-041 part 3d — per-trade auto-share-to-feed override. Defaults
  // checked; un-checking before submit makes THIS trade private (the
  // global users.auto_share_to_feed pref stays unchanged). PI lock-on
  // enforcement lives server-side (the global pref refuses to flip
  // off; per-trade override gating for PIs is a follow-up).
  const [shareToFeed, setShareToFeed] = useState(true);

  const [liveYesPrice, setLiveYesPrice] = useState<number>(
    yesOutcome?.currentPriceCents ?? 50,
  );
  const [liveNoPrice, setLiveNoPrice] = useState<number>(
    noOutcome?.currentPriceCents ?? 50,
  );
  const [priceFlash, setPriceFlash] = useState<Outcome | null>(null);
  // STORY-346 — live orderbook snapshot for the pre-flight liquidity
  // check. Refetched on market:trade events (debounced via the
  // existing socket subscription) so the warning reflects the latest
  // book without a separate WS room.
  const [orderbook, setOrderbook] = useState<OrderbookResponse | null>(null);

  // Cash balance in USDC cents (the matcher unit). For USDC/USD
  // wallets, the raw value IS USDC cents — no conversion. For
  // legacy BRL wallets, divide by the rate to get USDC cents
  // (1 BRL = 1/rate USDC at e.g. 5:1). When the rate is missing
  // and the wallet is BRL, we surface raw BRL cents — slightly off
  // numerically vs USDC, but better than 0 / blocking trading.
  const cashBalanceCents: number | null =
    cashBalanceRawCents === null
      ? null
      : walletCurrency === "BRL" && usdcPerBrl !== null && usdcPerBrl > 0
        ? Math.round(cashBalanceRawCents / usdcPerBrl)
        : cashBalanceRawCents;

  // STORY-A — locked_orders normalized to USDC cents the same way.
  const lockedOrdersCents: number | null =
    lockedOrdersRawCents === null
      ? null
      : walletCurrency === "BRL" && usdcPerBrl !== null && usdcPerBrl > 0
        ? Math.round(lockedOrdersRawCents / usdcPerBrl)
        : lockedOrdersRawCents;

  const totalBalanceCents: number | null =
    cashBalanceCents === null && lockedOrdersCents === null
      ? null
      : (cashBalanceCents ?? 0) + (lockedOrdersCents ?? 0);

  // STORY-710 / 2026-05-01 founder direction — pre-populate slippage
  // from the user's saved default unconditionally. Earlier behavior
  // only pre-filled values >2¢, leaving the field empty for the
  // common case ("no cap") because 2¢ was deemed too tight on the
  // closed-beta book. Founder feedback (UX audit 2026-04-30) was the
  // opposite: leaving "no cap" as the silent default means users
  // get blasted by bad fills they didn't expect. Smart default
  // wins; users who explicitly want unbounded fills can clear the
  // input. The friendlier slippage-rejection toast (next-step copy
  // landed today) handles the "didn't fill on thin depth" path.
  useEffect(() => {
    if (!loadTokens()) return;
    let cancelled = false;
    void api
      .getSlippagePrefs()
      .then((prefs) => {
        if (cancelled) return;
        if (maxSlippageCentsInput === "") {
          setMaxSlippageCentsInput(String(prefs.defaultMaxSlippageCents));
        }
      })
      .catch(() => {
        // silent — leave input empty
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // STORY-902b bug fix — load live taker fee rate. Public endpoint;
  // no auth required. Falls back silently to the default 200 bps
  // baked into the initial state if the request fails.
  useEffect(() => {
    let cancelled = false;
    void api
      .getPublicFees()
      .then((cfg) => {
        if (!cancelled) setTakerFeeBps(cfg.takerFeeBps);
      })
      .catch(() => {
        // silent — fee row stays at the default until reload
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!loadTokens()) return;
      try {
        const [allPositions, wallets, allOrders] = await Promise.all([
          api.listMyPositions(),
          api.listMyWallets().catch(() => null),
          // Bug-fix v2 2026-04-28 — fetch ALL the user's recent orders
          // and filter client-side. The previous version (status='open')
          // missed any order whose status had already transitioned out
          // of {open, partially_filled} but whose unfilled remainder
          // was still locked in `locked_positions` (e.g. some pending
          // matcher states). Pulling the unfiltered list and filtering
          // client-side on (side='sell' AND market matches AND outcome
          // matches AND remaining>0) is more robust to status-string
          // drift between API and matcher.
          api.listMyOrders().catch(() => [] as OrderRecord[]),
        ]);
        if (cancelled) return;
        setPositions(allPositions.filter((p) => p.marketId === market.id));

        // Sum unfilled SELL-order shares per outcome on this market.
        // Open SELL limits (and partially-filled ones) lock the
        // unfilled-shares portion in `locked_positions`; the position
        // row's `shares` count includes those locked shares, so the
        // user's actually-disposable count is shares − reserved.
        // Status filter is permissive — only exclude terminal states
        // (filled/cancelled/expired in legacy; matched/cancelled/unmatched
        // in new vocab) so transitional states still count as locking
        // shares. Routed through isTerminalStatus per STORY-813 PR 3.
        const reservedByOutcome: Record<string, number> = {};
        for (const o of allOrders) {
          if (o.side !== "sell") continue;
          if (o.status === "matched" || o.status === "cancelled" || o.status === "unmatched") continue;
          // 2026-05-12 — skip the order the user is editing. The
          // Edit submit path cancels it before placing the new one,
          // so the user's *effective* available pool already counts
          // those shares as free. Without this exclusion the modal
          // showed "30.42 held" while pre-filling the input with the
          // order's own 887.70 shares — coherence bug on a money
          // screen flagged by founder report 2026-05-11.
          if (editingOrderId && o.id === editingOrderId) continue;
          const oid = o.outcomeId ?? o.outcome_id;
          const mid = o.marketId ?? o.market_id;
          if (!oid || mid !== market.id) continue;
          const size = Number(o.size_shares);
          const filled = Number(o.filled_shares);
          const remaining = Math.max(0, size - filled);
          if (remaining > 0) {
            reservedByOutcome[oid] = (reservedByOutcome[oid] ?? 0) + remaining;
          }
        }
        setReservedShares(reservedByOutcome);

        if (wallets) {
          // 2026-05-09 — align to SiteHeader's pickPrimaryCashWallet
          // (the chrome surface picks based on displayCurrency, then
          // USDC, then any). Founder report: refilling test funds
          // credited the USDC wallet, header pill correctly showed
          // $200, but the trade panel was hard-coded to prefer BRL
          // wallet → showed $0.04 → "Not enough balance". Now both
          // surfaces use the same picker so they agree on which
          // wallet is "the user's money".
          const cash = pickPrimaryCashWallet(wallets, displayCurrency);
          setCashBalanceRawCents(cash ? cash.cents : 0);
          setWalletCurrency(cash?.currency ?? null);

          // STORY-A — locked_orders wallet matches whichever currency
          // the cash wallet picked, since reservations are denominated
          // in the same wallet's currency. Sum across currencies if
          // multiple exist; the fx layer above normalizes to BRL.
          const lockedWallets = wallets.filter(
            (w) => w.kind === "locked_orders",
          );
          const cashCurrency = cash?.currency ?? "BRL";
          const lockedMatching =
            lockedWallets.find((w) => w.currency === cashCurrency) ??
            lockedWallets[0] ??
            null;
          setLockedOrdersRawCents(
            lockedMatching ? Number(lockedMatching.balance_cents) : 0,
          );
        }

        // STORY-A — surface the per-order reservation list so the
        // user can expand "Reserved: $X" → see exactly which N orders
        // are tying up the money. The authoritative locked_orders
        // total is from the wallet row above; this list is the
        // "where's it locked" disclosure. Reserved is derived from
        // (remaining × price × 1.02) — the same formula the server
        // uses on placement (orders.service.ts:530-532). On partial
        // fills the per-row estimate decays naturally because we
        // recompute on `remainingShares`.
        setMyReservingOrders(
          allOrders
            .filter((o) => o.side === "buy" && o.status !== "matched" && o.status !== "cancelled" && o.status !== "unmatched")
            .map((o) => {
              const size = Number(o.size_shares);
              const filled = Number(o.filled_shares);
              const remaining = Math.max(0, size - filled);
              const price = Number(o.price_cents ?? 0);
              const notional = remaining * price;
              const reservedCents = Math.round(notional * 1.02);
              return {
                id: o.id,
                marketSlug: o.marketSlug ?? "—",
                side: o.side as "buy" | "sell",
                priceCents: price,
                remainingShares: remaining,
                reservedCents,
              };
            })
            .filter((o) => o.reservedCents > 0)
            .sort((a, b) => b.reservedCents - a.reservedCents),
        );
      } catch {
        // silent — affordances will show "—" on missing data
      }
    }
    void load();
    const onBalanceChanged = () => void load();
    window.addEventListener("granbay.balance.changed", onBalanceChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("granbay.balance.changed", onBalanceChanged);
    };
    // 2026-05-09 — `displayCurrency` added to deps. The picker no
    // longer keys off it (always-USDC post the wallet-picker fix),
    // but historically a cookie/region change caused the picker to
    // resolve a different wallet on re-mount; including the dep
    // here keeps the wallet load in sync if any future picker
    // logic re-introduces displayCurrency-sensitivity. Cheap fix
    // for the founder bug 2026-05-09 where the trade panel locked
    // on USDC at mount while the header re-resolved to BRL.
    //
    // 2026-05-12 — `editingOrderId` added because the reservedShares
    // aggregation reads it. Without it in deps, an Edit-modal mount
    // would compute reservedShares against the stale (no-exclusion)
    // closure and leave availableShares short of the order being
    // edited.
  }, [market.id, displayCurrency, editingOrderId]);

  // SELL+held-position auto-switch to shares mode (founder feedback
  // 2026-04-29). When positions load and the user is in SELL mode
  // on an outcome they actually hold, flip amountMode to 'shares'
  // — but ONLY if the URL didn't already pin the choice and only
  // ONCE per mount (so a user who manually toggles back to dollars
  // doesn't get yanked back to shares on every positions refresh).
  const sellSharesAutoSwitchRef = useRef(false);
  useEffect(() => {
    if (sellSharesAutoSwitchRef.current) return;
    if (urlPinnedAmountMode) return;
    if (mode !== "sell") return;
    if (positions.length === 0) return;
    const targetOutcomeId = side === "yes" ? yesOutcome?.id : noOutcome?.id;
    const pos = positions.find((p) => p.outcomeId === targetOutcomeId);
    const heldShares = pos ? Number(pos.shares) : 0;
    if (heldShares > 0) {
      sellSharesAutoSwitchRef.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAmountMode("shares");
    }
  }, [
    mode,
    side,
    positions,
    yesOutcome?.id,
    noOutcome?.id,
    urlPinnedAmountMode,
  ]);

  // One-click position close: when `?close=true` is in the URL,
  // pre-fill the amount with the user's full holding once positions
  // arrive. Fires once — if the user manually edits the amount we
  // don't clobber on a subsequent positions refresh. Tracked via the
  // closePrefillDoneRef ref instead of an effect dep to avoid the
  // re-run on amountInput changes.
  const closePrefillDoneRef = useRef(false);
  useEffect(() => {
    if (!closeIntent || closePrefillDoneRef.current) return;
    if (positions.length === 0) return;
    // 2026-05-12 — Edit-pending-sell prefill (DashboardTabs [Edit] click)
    // passes an explicit `initialAmount` equal to the existing order's
    // size. Don't clobber it with the gross holding here; the caller
    // already picked the right number. Mark the ref done so subsequent
    // refresh cycles also no-op.
    if (initialAmountProp) {
      closePrefillDoneRef.current = true;
      return;
    }
    const targetOutcomeId =
      initialSide === "yes" ? yesOutcome?.id : noOutcome?.id;
    const pos = positions.find((p) => p.outcomeId === targetOutcomeId);
    const heldShares = pos ? Number(pos.shares) : 0;
    if (heldShares > 0) {
      // setState in effect is intentional here: the prefill value
      // comes from an async API (listMyPositions), so we cannot
      // derive it during render. Guarded by closePrefillDoneRef so
      // it fires exactly once per mount.
      // 2026-05-11 founder report — heldShares is centi-shares
      // (post migration 0145); the input expects fractional whole-
      // shares (e.g. "77.98"). Pre-fix this set the input to the
      // raw centi count ("7798"), then the panel happily computed
      // notional as "7798 whole shares × 75¢ = $5,848.50" — 100×
      // overstated and definitively reject-able at the matcher.
      // Same migration-0145 sweep miss as the toasts + Fill plan
      // labels fixed in PR #1665.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAmountInput((heldShares / 100).toFixed(2));
      closePrefillDoneRef.current = true;
    }
  }, [
    closeIntent,
    positions,
    initialSide,
    yesOutcome?.id,
    noOutcome?.id,
    initialAmountProp,
  ]);

  useEffect(() => {
    const apiBase = process.env.NEXT_PUBLIC_API_URL;
    if (!apiBase || !market.slug) return;
    if (!yesOutcome || !noOutcome) return;
    const url = apiBase.replace(/\/$/, "");

    const socket: Socket = io(url, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
    });
    socket.on("connect", () => {
      socket.emit("subscribe", { slug: market.slug });
    });
    socket.on("market:trade", (event: IncomingTradeEvent) => {
      if (event.marketSlug !== market.slug) return;
      const isYesTrade = event.outcomeId === yesOutcome.id;
      const isNoTrade = event.outcomeId === noOutcome.id;
      if (!isYesTrade && !isNoTrade) return;
      if (isYesTrade) {
        setLiveYesPrice(event.priceCents);
        setLiveNoPrice(100 - event.priceCents);
      } else {
        setLiveNoPrice(event.priceCents);
        setLiveYesPrice(100 - event.priceCents);
      }
      setPriceFlash(isYesTrade ? "yes" : "no");
      setTimeout(() => setPriceFlash(null), 800);
      // STORY-346 — refresh the orderbook snapshot so the pre-flight
      // liquidity check sees the post-trade depth. Fire-and-forget;
      // failure isn't user-visible (the warning falls back to a
      // conservative "no data" branch).
      api
        .getOrderbook(market.slug)
        .then((book) => setOrderbook(book))
        .catch(() => {
          /* swallow */
        });
    });
    // STORY-347 / STORY-308 — non-fill order events (auto-convert
    // to limit, fresh resting limit, cancel) emit market:orderbook
    // but NOT market:trade, so the panel's preflight check would
    // stay on stale depth without this listener.
    socket.on("market:orderbook", (event: { marketSlug: string }) => {
      if (event.marketSlug !== market.slug) return;
      api
        .getOrderbook(market.slug)
        .then((book) => setOrderbook(book))
        .catch(() => {
          /* swallow */
        });
    });

    return () => {
      if (market.slug) socket.emit("unsubscribe", { slug: market.slug });
      socket.disconnect();
    };
  }, [market.slug, yesOutcome, noOutcome]);

  // STORY-346 — initial orderbook fetch + 2026-05-09 polling fallback.
  //
  // Founder report 2026-05-09: "the price is acting up again with no
  // dynamic." Root cause: this panel's orderbook state was refreshed
  // ONLY by WS `market:trade` / `market:orderbook` events. The
  // cloudflared tunnel is rejecting WSS upgrades on staging (issue
  // #1607), so when other users place orders the panel doesn't
  // resync — BUY YES / BUY NO buttons stay frozen on the initial-
  // mount snapshot. MarketMidpointDisplay + LiveMarketStats already
  // have polling fallbacks (PR #1604); the trade panel needed the
  // same posture for its orderbook read. Polls every 5s when the
  // tab is visible.
  useEffect(() => {
    if (!market.slug) return;
    let cancelled = false;

    function fetchBook() {
      api
        .getOrderbook(market.slug)
        .then((book) => {
          if (!cancelled) setOrderbook(book);
        })
        .catch(() => {
          /* swallow — warning falls back to the no-data branch */
        });
    }
    fetchBook();

    const pollHandle = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      fetchBook();
    }, 5_000);

    return () => {
      cancelled = true;
      clearInterval(pollHandle);
    };
  }, [market.slug]);

  // STORY-307d — Esc dismisses the high-value confirmation modal.
  // Bound at the window level so it works without focus inside the
  // modal. Only registers when confirming is true to avoid leaking
  // listeners.
  useEffect(() => {
    if (!confirming) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setConfirming(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirming]);

  if (!yesOutcome || !noOutcome) {
    return (
      <div className="rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-xs text-[var(--color-text3)]">
        Trading isn&apos;t available on multi-outcome markets yet.
      </div>
    );
  }

  // Buy/sell-aware best-quote display. Founder caught 2026-04-30: the
  // YES / NO outcome cards showed the same anchor price whether you'd
  // selected "Comprar" or "Vender" — a SELL view should display the
  // bid (what you receive) and a BUY view should display the ask (what
  // you'd pay). Falls back to liveYesPrice / liveNoPrice (last-trade
  // anchor / current_price_cents) when the orderbook hasn't loaded or
  // the relevant side is empty so the panel never renders blank.
  const bestQuoteFor = (
    outcomeKey: "yes" | "no",
    quoteSide: Mode,
    fallback: number,
  ): number => {
    if (!orderbook) return fallback;
    const book = outcomeKey === "yes" ? orderbook.yes : orderbook.no;
    const next = quoteSide === "buy" ? book.bestAsk : book.bestBid;
    return next ?? fallback;
  };
  const yesPrice = bestQuoteFor("yes", mode, liveYesPrice);
  const noPrice = bestQuoteFor("no", mode, liveNoPrice);
  // 2026-05-10 — `yesOppositePrice` / `noOppositePrice` removed.
  // The opposite-side spread badge inside each OutcomeCard duplicated
  // the bid/ask/spread row below the cards; the simplification pass
  // collapsed both into a single `<details>` "Order book details"
  // disclosure. The selected-side `bid` / `ask` / `delta` are computed
  // inline in the disclosure render below.
  // 2026-05-08 founder refinement — wide-spread threshold per
  // category. Mirrors ADR-042 (EPIC-051) defaults: sports /
  // politics / culture get a tighter 5¢ threshold; crypto /
  // geopolitics / world get 8¢ (more volatile, naturally wider).
  // Unknown / missing category falls through to the conservative
  // 5¢ default so the warning fires more often, not less.
  const wideSpreadThreshold = (() => {
    const slug = market.categorySlug ?? "";
    if (slug === "crypto" || slug === "geopolitics" || slug === "world") {
      return 8;
    }
    return 5;
  })();
  const sidePrice = side === "yes" ? yesPrice : noPrice;
  const limitPrice = limitPriceInput
    ? Math.max(1, Math.min(99, parseInt(limitPriceInput, 10) || sidePrice))
    : sidePrice;
  const effectivePrice = limitPrice;

  // Convert the unified amount input into a share count based on the
  // current amountMode. $ mode interprets the input as dollars and
  // computes centi-shares = floor(amount × 100 × 100 / price).
  // # mode treats the typed value as fractional shares and stores
  // centi-shares = floor(value × 100). Migration 0145 moved storage
  // to centi-share units (1 share = 100), so all `shares` math here
  // is in centi-shares end-to-end — backend stores it directly,
  // notional cost = floor(centi × price / 100), display divides
  // by 100 with 2-decimal precision.
  const amountNum = parseFloat(amountInput) || 0;
  const amountUsdcCents =
    amountMode === "dollars"
      ? Math.round(amountNum * 100 * displayToUsdcRatio)
      : 0;
  // `shares` here is CENTI-shares (1 whole share = 100). The variable
  // name stays for diff continuity; UI renderers below format as
  // `(shares / 100).toFixed(2)`.
  //
  // 2026-05-11 founder report (round 2 on the $1→$0.98 / R$1→R$0,95
  // residual): the old `Math.floor` always rounded DOWN, so the actual
  // notional cost was always strictly less than the user's typed input.
  // In BR locale the gap was visible enough (R$1 → R$0,95) to read as
  // a bug. The original fix shipped a "Round up to R$1,05" chip
  // (#1629) as an opt-in; founder pushed back — the default should
  // produce a cost as close to the typed amount as possible without
  // requiring a click.
  //
  // New behaviour: pick whichever of {floor, ceil} centi-share counts
  // produces a cost CLOSEST to the user's input. Tiebreak to ceil so
  // a "round number" input (R$1.00) snaps UP rather than DOWN, which
  // matches the founder's intent ("R$1 should be 1.0xx, not 0,95").
  // Bounded by cashBalanceCents — if ceil would over-reserve, fall
  // back to floor so the order still places. Max-affordable button
  // and balance-check downstream remain in floor space.
  const shares = (() => {
    if (amountMode !== "dollars") {
      return Math.floor(amountNum * 100);
    }
    if (effectivePrice <= 0 || amountUsdcCents <= 0) {
      return 0;
    }
    const flooredShares = Math.floor((amountUsdcCents * 100) / effectivePrice);
    const ceilShares = flooredShares + 1;
    const flooredCost = Math.floor((flooredShares * effectivePrice) / 100);
    const ceilCost = Math.floor((ceilShares * effectivePrice) / 100);
    const distFloor = Math.abs(amountUsdcCents - flooredCost);
    const distCeil = Math.abs(amountUsdcCents - ceilCost);
    // Cap at user's balance via the per-share reservation envelope. If
    // ceil would cost more than the user has (BUY only — SELL doesn't
    // touch cash), fall back to floor regardless of which is closer.
    const feeMultiplier = 1 + takerFeeBps / 10000;
    const ceilReservation = Math.ceil(ceilCost * feeMultiplier);
    const ceilExceedsBalance =
      mode === "buy" &&
      cashBalanceCents !== null &&
      ceilReservation > cashBalanceCents;
    if (ceilExceedsBalance) return flooredShares;
    return distCeil <= distFloor ? ceilShares : flooredShares;
  })();

  const yesPosition = positions.find((p) => p.outcomeId === yesOutcome.id);
  const noPosition = positions.find((p) => p.outcomeId === noOutcome.id);
  const currentPosition = side === "yes" ? yesPosition : noPosition;
  // Bug-fix 2026-04-28 — disposable held shares = position shares MINUS
  // shares locked by the user's open SELL limit orders on this outcome.
  // Without this subtraction the trade panel claims you hold 87 even
  // after you've placed a sell-35 limit, and the validation lets you
  // place a second sell that would over-reserve at the matcher.
  const currentOutcomeId = side === "yes" ? yesOutcome.id : noOutcome.id;
  const grossShares = currentPosition ? Number(currentPosition.shares) : 0;
  const reservedForOutcome = reservedShares[currentOutcomeId] ?? 0;
  const availableShares = Math.max(0, grossShares - reservedForOutcome);

  // Cost / proceeds math. `shares` is centi-shares (post migration
  // 0145), so notional = floor(centi × price / 100) — same shape as
  // the matcher's centiShareNotionalCents helper.
  const notionalCents = Math.floor((shares * effectivePrice) / 100);

  // Founder report 2026-05-10 — when the user types R$1 in BR locale
  // and the price is 26¢, the centi-share floor produces 76 centi (the
  // "shares" var above) costing R$0,95. The 5¢ residual reads as a
  // bug ("I typed R$1, why does it say R$0,95?"). The structural
  // truth is the USDC ledger floors twice (centi-share count + sub-
  // cent rounding); proper UX is to surface a "round up" affordance
  // — Polymarket pattern — that lets the user click once and land
  // on the next round amount that perfectly consumes a whole centi.
  // Computed only when there's actual residual (no chip otherwise),
  // and capped so we don't suggest absurd round-ups when the price
  // is so high that the next centi costs 5× the input.
  const roundUpHint = (() => {
    if (mode !== "buy" || amountMode !== "dollars") return null;
    if (shares <= 0 || effectivePrice <= 0) return null;
    if (displayToUsdcRatio <= 0) return null;
    const typedDisplayCents = Math.round(amountNum * 100);
    if (typedDisplayCents <= 0) return null;
    const actualDisplayCents = Math.round(notionalCents / displayToUsdcRatio);
    const residual = typedDisplayCents - actualDisplayCents;
    if (residual <= 0) return null;
    const nextShares = shares + 1;
    const nextNotionalUsdcCents = Math.ceil(
      (nextShares * effectivePrice) / 100,
    );
    const nextDisplayCents = Math.ceil(
      nextNotionalUsdcCents / displayToUsdcRatio,
    );
    if (nextDisplayCents <= typedDisplayCents) return null;
    if (nextDisplayCents - typedDisplayCents > residual * 3) return null;
    return {
      nextDisplayValue: (nextDisplayCents / 100).toFixed(2),
      nextSharesLabel: (nextShares / 100).toFixed(2).replace(/\.?0+$/, ""),
    };
  })();
  // Display helper — format centi-share count as a fractional shares
  // string (1.03, 12.50, etc.). Trims trailing .00 so whole-share
  // counts read clean ("3" not "3.00") while fractional counts get
  // 2-decimal precision.
  const fmtShares = (centi: number): string => {
    const whole = centi / 100;
    if (Number.isInteger(whole)) return String(whole);
    return whole.toFixed(2);
  };
  // STORY-902b bug fix 2026-04-29 — read takerFeeBps from
  // /v1/platform/fees state instead of the previously hardcoded 200
  // bps. Admin updates via /admin/fees now propagate to this row +
  // the You-pay total in real time.
  const feeCents = Math.floor((notionalCents * takerFeeBps) / 10000);
  const totalCents = notionalCents + feeCents;
  // STORY-812 phase 3d — orderType is permanently 'limit' so the
  // legacy worst-case-envelope (`shares * 99 * feeMultiplier`) for
  // market BUY is dead. Reservation always equals the user's
  // notional + fee at the chosen max-price; the matcher will refund
  // any excess via standard order_release legs at fill time when
  // the book offers a price improvement. Disclosure also goes
  // away — the summary's headline cost == reservation now.
  const reservationCents = totalCents;
  // ESTIMATED cost — what the user actually pays at the current
  // effective price. This is what the summary "You pay" line shows.
  const estimatedCostCents = totalCents;

  // STORY-307d — two-step confirmation for high-value orders.
  // Trigger if EITHER threshold is exceeded:
  //   - reservation > 10% of cash balance ("meaningful chunk of wallet")
  //   - reservation > R$500 absolute ("meaningful regardless of wealth")
  // P1 / Gate 3 blocker; closed-beta virtual money makes a fat-finger
  // forgiving but still annoying — the modal also catches accidental
  // 10× share inputs that pass validation.
  const HIGH_VALUE_ABS_CENTS = 50_000; // R$500
  const HIGH_VALUE_REL_PCT = 0.1;
  const triggersAbsoluteThreshold = reservationCents > HIGH_VALUE_ABS_CENTS;
  const triggersRelativeThreshold =
    cashBalanceCents !== null &&
    cashBalanceCents > 0 &&
    reservationCents > Math.floor(cashBalanceCents * HIGH_VALUE_REL_PCT);
  const isHighValueOrder =
    shares > 0 && (triggersAbsoluteThreshold || triggersRelativeThreshold);
  const netProceedsCents = notionalCents - feeCents;
  // Migration 0145 — shares is centi-shares (×100 from whole), and
  // each centi-share resolves to $0.01 = 1 cent at full settlement,
  // so payout in cents equals the centi-share count directly. The
  // old `× 100` multiplier was the "1 share = $1.00 = 100 cents"
  // factor; now it's baked into the unit.
  const maxPayoutCents = shares;
  const profitCents =
    mode === "buy" ? maxPayoutCents - totalCents : netProceedsCents;
  const profitPct =
    mode === "buy" && totalCents > 0 ? (profitCents / totalCents) * 100 : 0;

  const clientInsufficientBalance =
    mode === "buy" &&
    shares > 0 &&
    cashBalanceCents !== null &&
    reservationCents > cashBalanceCents;

  // 2026-05-09 — also catch the SERVER-returned insufficient-balance
  // 400 so the UX is consistent. Race: user clicks Place Buy with the
  // client thinking it can afford; the server's reservation math
  // rejects ($shares × limit × 1.02 includes fee headroom that the
  // client's "you pay" estimate may not). Without this routing, the
  // server message lands in the red `error` block; founder reported
  // the dissonance ("why is this a hostile red error when the panel
  // already has a softer 'Buy what you can' UX?"). Now both client
  // pre-check + server reject share the same blue/info shape with the
  // applyMaxBuy CTA.
  const serverInsufficientBalance =
    mode === "buy" &&
    typeof error === "string" &&
    /insufficient balance/i.test(error);

  const insufficientBalance =
    clientInsufficientBalance || serverInsufficientBalance;

  // Quick-chip values in DISPLAY currency. Per-region ladder:
  //   USD/USDC: $25 / $100 / $500 (lowest tier matches mid-volume trade
  //     sizes typical of US/global crypto-prediction users)
  //   BRL:      R$1 / R$25 / R$500 (entry-tier of R$1 acknowledges
  //     Brazilian PIX micro-transaction norms — common to bet R$1-5 on
  //     conviction trades; mid + high tiers unchanged)
  // displayToUsdcRatio turns the typed display-cents into the USDC-cent
  // matcher unit; no separate per-chip conversion needed.
  const dollarChips =
    displayCurrency === "BRL" ? [1, 25, 500] : [25, 100, 500];
  const shareChips = [100, 500, 1000];
  // 2026-05-11 — eToro-style partial-close percentage chips, founder
  // direction "adding only the partial percentage partial close orders
  // like etoro." Active in SELL mode when the user has a position +
  // amountMode='shares'. Replaces the prior ClosePositionModal-only
  // surface so close-position becomes a TradePanel flow like Buy /
  // Sell — one surface, one code path. 100% is the same as Max but
  // labelled to match the eToro convention users recognize.
  const percentChips: Array<25 | 50 | 75 | 100> = [25, 50, 75, 100];
  const showPercentChips =
    mode === "sell" && amountMode === "shares" && availableShares > 0;

  function applyChip(value: number) {
    setAmountInput(String(value));
  }

  function applyPercentChip(pct: 25 | 50 | 75 | 100) {
    // availableShares is centi-shares; chip computes integer-centi
    // amount then renders as fractional whole-shares for the input
    // (e.g. "844.04"). 100% maps to the same value applyMaxSell
    // produces — kept distinct for readability + symmetry with the
    // 25/50/75 chips.
    const centiToSell = Math.floor((availableShares * pct) / 100);
    setAmountInput((centiToSell / 100).toFixed(2));
  }

  function applyMaxBuy() {
    if (cashBalanceCents === null || effectivePrice <= 0) return;
    // Founder bug 2026-05-02 — Max used to divide by a hardcoded
    // 1.02 (2% fee), so a $21.30 balance + 2.5% fee schedule
    // populated $20.88 instead of $20.78, then "You pay $21.41"
    // exceeded the $21.30 reservation gate and left the CTA
    // disabled with no way out. Now works backwards from the
    // actual `takerFeeBps` × the per-share reservation
    // (effectivePrice × feeMultiplier — orderType is permanently
    // 'limit' post-STORY-812 phase 3d, so the legacy 99¢ market
    // worst-case branch is gone).
    const feeMultiplier = 1 + takerFeeBps / 10000;
    // Migration 0145 — operate in centi-shares throughout. Per-centi
    // reservation = price × fee × (1/100) since each centi-share
    // costs 1/100 of a whole-share's price. We keep the same gating
    // formula (cents available / per-unit cost), just at centi
    // granularity for the shares variable.
    const perCentiReservationCents = (effectivePrice * feeMultiplier) / 100;
    if (perCentiReservationCents <= 0) return;
    const maxSharesAffordable = Math.floor(
      cashBalanceCents / perCentiReservationCents,
    );
    if (amountMode === "dollars") {
      // Notional in USDC cents from the safe share count, converted
      // back to displayCurrency cents for the input field. shares
      // are centi (1 share = 100), so notional = floor(centi × price
      // / 100).
      const notionalUsdcCents = Math.floor(
        (maxSharesAffordable * effectivePrice) / 100,
      );
      const notionalDisplayCents =
        displayCurrency === "BRL" && usdcPerBrl !== null && usdcPerBrl > 0
          ? Math.round(notionalUsdcCents * usdcPerBrl)
          : notionalUsdcCents;
      setAmountInput(String((notionalDisplayCents / 100).toFixed(2)));
    } else {
      // # mode — input is fractional shares (e.g. "3.84"); divide
      // centi-share affordable count by 100.
      setAmountInput((maxSharesAffordable / 100).toFixed(2));
    }
  }

  function applyMaxSell() {
    if (amountMode === "dollars") {
      // availableShares is centi-shares (post migration 0145); notional
      // cents = floor(centi × price / 100), same shape as the matcher.
      const grossUsdcCents = Math.floor(
        (availableShares * effectivePrice) / 100,
      );
      const grossDisplayCents =
        displayCurrency === "BRL" && usdcPerBrl !== null && usdcPerBrl > 0
          ? Math.round(grossUsdcCents * usdcPerBrl)
          : grossUsdcCents;
      setAmountInput(String((grossDisplayCents / 100).toFixed(2)));
    } else {
      // # mode: input represents fractional shares (e.g., "3.84").
      // availableShares is centi-shares, so divide by 100.
      setAmountInput((availableShares / 100).toFixed(2));
    }
  }

  function selectOutcome(next: Outcome) {
    // Logged-out users clicking a SELL card go straight to /login
    // with the intent encoded so they round-trip back into sell
    // mode on the same outcome. Mirrors the Sell-tab redirect
    // and the form-submit redirect for consistency.
    if (mode === "sell" && !loadTokens()) {
      redirectToLoginWithIntent(next);
      return;
    }
    setSide(next);
    setLimitPriceInput("");
    // 2026-05-12 — clear the amount on side toggle. Without this the
    // previous side's amount carried over: a user who clicked Edit on
    // the NO row (amount prefilled to 77.98 — their NO pending sell)
    // then switched to SELL YES saw 77.98 sitting in the YES form
    // while the "You hold:" line read 30.42 YES shares. The number
    // pair is irreconcilable on a money screen.
    setAmountInput("");
    setError(null);
  }

  // Helper: build the same return-URL the form-submit path uses and
  // push to /login. Reused by the Sell tab + Sell-card click gates
  // so logged-out users always round-trip back to where they were.
  function redirectToLoginWithIntent(targetSide: Outcome = side) {
    const params = new URLSearchParams({
      trade: "sell",
      outcome: targetSide,
      type: orderType,
      amountMode,
      amount: amountInput,
    });
    if (limitPriceInput) {
      params.set("price", limitPriceInput);
    }
    const returnTo = `/markets/${market.slug}?${params.toString()}`;
    router.push(`/login?next=${encodeURIComponent(returnTo)}`);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (shares <= 0) {
      setError("Enter an amount.");
      return;
    }
    if (mode === "sell" && shares > availableShares) {
      // STORY-812 phase 1 — over-sell guidance per founder UX ask
      // 2026-05-01. Just blocking the action wasn't enough; users
      // who try to sell more than they own are usually trying to
      // express the OPPOSITE conviction (Bet against). Surface the
      // workflow they actually want: "Buy <opposite> shares instead
      // to take the opposite position."
      const oppositeSide = side === "yes" ? "NO" : "YES";
      const sideUpper = side.toUpperCase();
      setError(
        availableShares === 0
          ? `You don't hold any ${sideUpper} shares on this market. To take the opposite position, switch to Buy and pick ${oppositeSide}.`
          : `You only have ${fmtShares(availableShares)} ${sideUpper} share(s) available. To go further short, switch to Buy and pick ${oppositeSide}.`,
      );
      return;
    }
    if (!loadTokens()) {
      // Polymarket-style: round-trip through /login and return the
      // user to this exact market with the buy/sell intent prefilled,
      // not to /home. Encode the full panel state into the URL so
      // the prefill on return is one click away from confirming.
      const params = new URLSearchParams({
        trade: mode,
        outcome: side,
        type: orderType,
        amountMode,
        amount: amountInput,
      });
      if (limitPriceInput) {
        params.set("price", limitPriceInput);
      }
      const returnTo = `/markets/${market.slug}?${params.toString()}`;
      router.push(`/login?next=${encodeURIComponent(returnTo)}`);
      return;
    }

    // STORY-307d — high-value orders open a confirmation modal instead
    // of submitting in one click. The modal's Confirm button calls
    // executePlaceOrder() directly. Below the threshold, we fall
    // through to immediate submission.
    if (isHighValueOrder) {
      setConfirming(true);
      return;
    }
    await executePlaceOrder();
  }

  async function executePlaceOrder() {
    if (!yesOutcome || !noOutcome) return;
    setSubmitting(true);
    try {
      // 2026-05-12 — Edit-pending-sell flow (DashboardTabs [Edit] click).
      // Cancel the original order BEFORE placing the new one so the
      // matcher sees the freed shares when it reservation-checks the
      // replacement. If the cancel fails, surface the error and abort
      // the place call — leaving the user with their original order
      // intact is strictly better than placing an additional order
      // that doubles their net reservation.
      if (editingOrderId) {
        try {
          await api.cancelOrder(editingOrderId);
        } catch (err) {
          setError(
            err instanceof Error
              ? `Couldn't cancel the existing order to replace it: ${err.message}`
              : "Couldn't cancel the existing order to replace it.",
          );
          setSubmitting(false);
          return;
        }
      }
      const outcomeId = side === "yes" ? yesOutcome.id : noOutcome.id;
      // STORY-708 — thread maxSlippageCents through for market orders.
      // Limit orders ignore it (their priceCents already enforces a
      // ceiling). Empty input = no slippage param = STORY-307c worst-
      // case envelope (the legacy default). With orderType pinned to
      // 'limit' post-STORY-812 phase 3d, the slippage field is now
      // dead — the limit price already caps acceptable fills — but
      // the matcher still respects an explicit value if the user
      // typed one. Kept as a passthrough until STORY-817 (power-user
      // advanced toggle) decides the surface explicitly.
      const parsedSlippage = parseInt(maxSlippageCentsInput, 10);
      const slippagePayload =
        Number.isFinite(parsedSlippage) &&
        parsedSlippage >= 1 &&
        parsedSlippage <= 99
          ? { maxSlippageCents: parsedSlippage }
          : {};
      // STORY-812 phase 2 — explicit executionFlag. The matcher
      // (STORY-810/811) decides actual fill behavior from this flag,
      // not from orderType. We always send 'GTC' here because the
      // unified flow is "always a limit"; the matcher itself smart-
      // routes (FAK if the price crosses the book, GTC if it rests).
      // Phase 3 will plumb the FAK/FOK power-user options through
      // when the advanced toggle ships (STORY-817).
      const placed = await api.placeOrder({
        marketId: market.id,
        outcomeId,
        side: mode,
        orderType,
        executionFlag: "GTC",
        priceCents: effectivePrice,
        sizeShares: shares,
        // EPIC-041 part 3d — only pass the override when the user
        // un-checked. Letting `undefined` flow through keeps the
        // server falling back to users.auto_share_to_feed for the
        // common (default-checked) path.
        ...(shareToFeed ? {} : { shareToFeed: false }),
        // EPIC-031 STORY-1102 phase 2 — attribution to whichever
        // builder this trader arrived through. `useBuilderRefCode`
        // sources the code from URL ?bc=<hex> on the first hit and
        // persists it in the granbay.bc cookie for 90 days. Omitted
        // when no code is captured (common case — direct visit).
        ...(builderRefCode ? { builderCodeHex: builderRefCode } : {}),
        ...slippagePayload,
      });
      // STORY-708 — surface the structured slippage rejection. The
      // backend attaches `slippageRejection` to the order row when
      // shares were unfilled because the matcher refused fills outside
      // the user's tolerance band. We treat this as a 'warning' tone
      // toast — not an error — because the order itself succeeded
      // (it just didn't fill all shares at the price the user wanted).
      if (placed?.slippageRejection) {
        const r = placed.slippageRejection;
        // 2026-04-30 founder UX pass — replace jargon ("anchor",
        // "tolerance", "cap") with plain language. The user just
        // needs to know: how many shares filled, the best price
        // we could find, and what to do next.
        const verbed = mode === "buy" ? "couldn't buy" : "couldn't sell";
        const filled = Number(placed.filled_shares ?? 0);
        // 2026-05-11 — passed `filled` + `unfilledShares` are centi-
        // shares from the matcher; the t-template interpolates them
        // directly. Convert to fractional whole-shares via fmtShares
        // before passing so the user-facing copy reads "1.72" not
        // "172".
        const detail =
          filled > 0
            ? t("tradePanel.slippageDetailPartial", {
                filled: fmtShares(filled),
                unfilled: fmtShares(Number(r.unfilledShares)),
                bestPrice: r.effectivePriceCents,
                maxPrice: r.maxAllowedCents,
                verbed,
              })
            : t("tradePanel.slippageDetailNone", {
                unfilled: fmtShares(Number(r.unfilledShares)),
                bestPrice: r.effectivePriceCents,
                maxPrice: r.maxAllowedCents,
                verbed,
              });
        setSuccess({
          title: t("tradePanel.slippageTitle"),
          detail,
          tone: "warning",
        });
        setFormPulse(true);
        setTimeout(() => setFormPulse(false), 900);
        setTimeout(() => setSuccess(null), 12000);
        window.dispatchEvent(new CustomEvent("granbay.balance.changed"));
        // 2026-04-30 — preserve amountInput on slippage rejection so
        // the user can re-submit with a higher tolerance without
        // re-typing the amount. The previous setAmountInput("")
        // cleared the field while the toast still referenced N
        // unfilled shares, so the input read 0.00 next to a message
        // about 35 shares — confusing state mismatch.
        try {
          const all = await api.listMyPositions();
          setPositions(all.filter((p) => p.marketId === market.id));
        } catch {
          // non-fatal
        }
        router.refresh();
        setSubmitting(false);
        return;
      }
      const verb =
        mode === "buy"
          ? t("tradePanel.confirmModalVerbBuy")
          : t("tradePanel.confirmModalVerbSell");
      // The placeOrder response carries the order's post-matcher
      // state. Use it to disambiguate three cases the user cares
      // about: full fill, partial / open, and zero-fill cancelled
      // (the last is what tripped up the testing scenario where a
      // market order had no opposing liquidity from other users).
      // STORY-812 phase 3c (founder ask 2026-05-01) — post-
      // execution status display. Polymarket pattern:
      //   ✓ Filled: 50 shares at 28¢ ($14.00)
      //   ○ Pending: 39 shares at 28¢ ($10.92) [Cancel pending]
      // We compute the same shape from the place-order response
      // and surface it in the success toast. The "Cancel pending"
      // affordance only renders when there's a resting remainder
      // and we have the order id to call cancelOrder against.
      const filledShares = Number(placed?.filled_shares ?? "0");
      const totalShares = Number(placed?.size_shares ?? shares);
      const pendingShares = Math.max(0, totalShares - filledShares);
      const finalStatus = placed?.status ?? "live";
      const fillPriceCents = placed?.avg_fill_price_cents ?? effectivePrice;
      if (finalStatus === "matched" || pendingShares === 0) {
        setSuccess({
          title: `${verb} order filled`,
          detail: `✓ ${fmtShares(filledShares)} ${side.toUpperCase()} at avg ${fillPriceCents}¢`,
          tone: "success",
        });
      } else if (filledShares > 0) {
        // Partial: some fills + resting remainder.
        setSuccess({
          title: `${verb} order placed`,
          detail: `✓ ${fmtShares(filledShares)} filled at avg ${fillPriceCents}¢ · ○ ${fmtShares(pendingShares)} resting at ${effectivePrice}¢`,
          tone: "success",
          pendingOrderId: placed?.id,
          pendingShares,
        });
      } else if (finalStatus === "cancelled") {
        // FAK cancelled (no fills, no resting). STORY-B-frontend
        // (#1558) — when the server attached a `nonFillReason`,
        // map it to specific copy. Otherwise fall back to the
        // generic message.
        const reason = placed?.nonFillReason ?? null;
        const oppositeSide = mode === "buy" ? "NO BUY" : "YES BUY";
        let detail: string;
        if (reason === "self_trade_only_liquidity") {
          detail = `Your own ${oppositeSide} at ${effectivePrice}¢ is the only liquidity here. Cancel it from the Orders tab on /home or wait for another trader.`;
        } else if (reason === "price_out_of_range") {
          detail = `No offers in your price range — best ${mode === "buy" ? "ask" : "bid"} is outside ${effectivePrice}¢. Adjust your max price or wait for counter-side liquidity.`;
        } else if (reason === "no_resting_orders") {
          detail = `No resting orders on this side yet. Place a limit instead — yours will rest as the first ${mode === "buy" ? "bid" : "ask"} at ${effectivePrice}¢.`;
        } else if (reason === "partial_fill_remainder_cancelled") {
          detail = `${fmtShares(filledShares)} of ${fmtShares(totalShares)} shares filled at avg ${fillPriceCents}¢. Remainder cancelled (market-order semantics).`;
        } else {
          detail = `No offers ${mode === "buy" ? "at or below" : "at or above"} ${effectivePrice}¢. Adjust your max price or wait for counter-side liquidity.`;
        }
        setSuccess({
          title: `${verb} order didn't fill`,
          detail,
          tone: "warning",
        });
      } else {
        // status='open' with 0 fills — whole order rests as a GTC
        // limit at the user's price. This is the routing case for
        // a limit that doesn't cross the book.
        // STORY-B-frontend — append a nonFillReason hint so the
        // user understands why a limit at a tight price isn't
        // crossing immediately (most common: self-trade).
        const reason = placed?.nonFillReason ?? null;
        const oppositeSide = mode === "buy" ? "NO BUY" : "YES BUY";
        const reasonHint =
          reason === "self_trade_only_liquidity"
            ? ` — your own ${oppositeSide} at the same price won't cross against you`
            : reason === "price_out_of_range"
              ? ` — no offers in range; you may want a ${mode === "buy" ? "higher max" : "lower min"} price`
              : "";
        setSuccess({
          title: `${verb} limit placed`,
          detail: `○ ${fmtShares(pendingShares)} ${side.toUpperCase()} resting at ${effectivePrice}¢${reasonHint}`,
          tone: "success",
          pendingOrderId: placed?.id,
          pendingShares,
        });
      }
      setFormPulse(true);
      setTimeout(() => setFormPulse(false), 900);
      setTimeout(() => setSuccess(null), 6000);
      window.dispatchEvent(new CustomEvent("granbay.balance.changed"));
      // Page-level toast hook (OrderPlacedToast) — fires regardless
      // of where TradePanel is mounted (market page or dashboard
      // modal). Same payload shape the close-modal dispatched before
      // this refactor.
      window.dispatchEvent(
        new CustomEvent("granbay.order.placed", {
          detail: {
            side: mode === "buy" ? "buy" : "sell",
            orderId: placed?.id ?? null,
            filledShares,
            pendingShares,
            requestedShares: totalShares,
            priceCents: fillPriceCents ?? effectivePrice,
            isResting: finalStatus === "live",
            marketSlug: market.slug,
            outcomeSortOrder: side === "yes" ? 0 : 1,
          },
        }),
      );
      setAmountInput("");
      try {
        const all = await api.listMyPositions();
        setPositions(all.filter((p) => p.marketId === market.id));
      } catch {
        // non-fatal
      }
      router.refresh();
      // Modal host (TradePanelModal) listens for this to auto-dismiss
      // on a successful place — same UX promise the standalone
      // ClosePositionModal used to deliver (close on success). The
      // inline `success` toast above already gives the user the
      // fills/resting feedback in-panel; dismissal completes the
      // Polymarket-pattern action surface contract.
      onOrderPlaced?.();
    } catch (err) {
      // EPIC-050 STORY-2201 — Tier-1 light KYC gate. The backend
      // returns Tier1KycRequired when the user hasn't promoted
      // beyond Tier 0; the hook redirects to /kyc/tier1?next=...
      // and we bail before showing the inline-error state.
      if (kycRedirect(err)) return;
      if (err instanceof ApiError) {
        setError(
          err.status === 401
            ? "Your session expired. Please log in again."
            : err.message,
        );
      } else {
        setError(err instanceof Error ? err.message : "Couldn't place order.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  // STORY-346 → STORY-812 phase 3b — pre-flight fill plan.
  // Walks the relevant side of the orderbook respecting the user's
  // limit price cap (BUY: only asks ≤ price, SELL: only bids ≥
  // price), reports how many shares fill immediately vs how many
  // would rest as a GTC limit at the user's price. Renders below
  // the limit-price field as a "Fill plan" panel so the user
  // confirms the routing decision (smart routing per Polymarket
  // study) before submitting.
  const preflight =
    shares > 0 && orderbook
      ? computePreflightFill({
          orderbook,
          side: mode,
          outcome: side,
          requestedShares: shares,
          fullFillThresholdPct: 80,
          maxAcceptablePriceCents: effectivePrice,
        })
      : null;

  const ctaLabel = (() => {
    if (submitting) return "Placing order…";
    if (shares <= 0) {
      return mode === "buy"
        ? `Buy ${side.toUpperCase()}`
        : `Sell ${side.toUpperCase()}`;
    }
    const totalCents = mode === "buy" ? reservationCents : netProceedsCents;
    return `${mode === "buy" ? "Place Buy" : "Place Sell"} · ${fmtFx(totalCents)}`;
  })();

  const ctaBg =
    mode === "sell"
      ? "var(--color-no)"
      : side === "yes"
        ? "var(--color-yes)"
        : "var(--color-no)";

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-[10px] border bg-[var(--color-surface)] p-3.5 transition-colors duration-700"
      style={{
        borderColor: formPulse ? "var(--color-yes)" : "var(--color-border)",
        boxShadow: formPulse
          ? "0 0 0 3px color-mix(in srgb, var(--color-yes) 15%, transparent)"
          : undefined,
      }}
    >
      {/* STORY-812 phase 2 (2026-05-01) — Market/Limit toggle
          removed per Polymarket comparison study. Single Buy/Sell
          flow: user enters their max price; the matcher smart-
          routes (immediate fill if the price crosses the book,
          rests as a GTC limit otherwise). The legacy Zap (market)
          + Pin (limit) icons + i18n keys 'tradePanel.market' /
          'tradePanel.limit' are now unused; cleanup in phase 3
          alongside the asymmetric input + fill plan preview. */}

      {/* Mode toggle — Buy / Sell. Hidden in compact (modal) mode
          because the dashboard "Close" entrypoint is sell-only —
          showing a Buy toggle would invite the user to buy more of a
          position they're trying to exit. */}
      {!compact && (
        <div className="mb-3 flex items-center justify-between">
          <div className="flex gap-3 text-[11px]">
            <button
              type="button"
              onClick={() => {
                setMode("buy");
                setAmountInput("");
                setError(null);
                // BUY defaults to dollars regardless of prior SELL state.
                // Without this, a user who toggled SELL → shares and then
                // clicks BUY would type a "share count" into a dollars-
                // interpreted field — see commit message for repro.
                if (!urlPinnedAmountMode) {
                  setAmountMode("dollars");
                }
              }}
              className={`font-semibold transition-colors ${
                mode === "buy"
                  ? "text-[var(--color-text)] underline underline-offset-4"
                  : "text-[var(--color-text3)] hover:text-[var(--color-text)]"
              }`}
            >
              {t("tradePanel.buy")}
            </button>
            <button
              type="button"
              onClick={() => {
                // Founder request 2026-04-28 — logged-out users clicking
                // Sell go straight to /login with sell intent encoded so
                // they round-trip back into sell mode on the same market.
                if (!loadTokens()) {
                  redirectToLoginWithIntent(side);
                  return;
                }
                setMode("sell");
                setAmountInput("");
                setError(null);
                // SELL on a held outcome should default to shares mode
                // (matches the "You hold: X shares" hint right above the
                // input). The mount-only auto-switch effect can't see
                // this click, so cascade the same logic here.
                if (!urlPinnedAmountMode) {
                  const targetOutcomeId =
                    side === "yes" ? yesOutcome?.id : noOutcome?.id;
                  const pos = positions.find(
                    (p) => p.outcomeId === targetOutcomeId,
                  );
                  const heldShares = pos ? Number(pos.shares) : 0;
                  setAmountMode(heldShares > 0 ? "shares" : "dollars");
                }
              }}
              className={`font-semibold transition-colors ${
                mode === "sell"
                  ? "text-[var(--color-text)] underline underline-offset-4"
                  : "text-[var(--color-text3)] hover:text-[var(--color-text)]"
              }`}
            >
              {t("tradePanel.sell")}
            </button>
          </div>
        </div>
      )}

      {/* STORY B (2026-05-14) — bound-outcome banner. Renders when the
          panel is mounted on a specific outcome of a multi-outcome
          market so the user always sees which outcome of the ladder
          is currently driving the trade form. The label format —
          "Trading: <Name> · Yes Xc / No Yc" — comes from the caller
          (MultiOutcomeTradePanel) and is rendered verbatim. */}
      {boundOutcome?.label &&
        (() => {
          // Resolver chain: explicit iconUrl (image or emoji: sentinel)
          // → outcome-icons fallback (country flag / concept glyph /
          // colored initials bubble). Same chain as OutcomeRowList so
          // the banner stays consistent with the per-outcome rows.
          let iconNode: React.ReactNode = null;
          if (
            boundOutcome.iconUrl &&
            !boundOutcome.iconUrl.startsWith("emoji:")
          ) {
            iconNode = (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={boundOutcome.iconUrl}
                alt=""
                className="h-5 w-5 flex-shrink-0 rounded-sm object-cover"
              />
            );
          } else if (boundOutcome.iconUrl?.startsWith("emoji:")) {
            iconNode = (
              <span
                aria-hidden="true"
                className="text-[18px] leading-none"
              >
                {boundOutcome.iconUrl.slice("emoji:".length)}
              </span>
            );
          } else {
            const fallback = getOutcomeIcon(boundOutcome.label);
            iconNode =
              fallback.kind === "emoji" ? (
                <span
                  aria-hidden="true"
                  className="text-[18px] leading-none"
                >
                  {fallback.emoji}
                </span>
              ) : (
                <span
                  aria-hidden="true"
                  className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
                  style={{ background: fallback.color }}
                >
                  {fallback.initials}
                </span>
              );
          }
          return (
            <div
              className="mb-3 flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-[11px] text-[var(--color-text2)]"
              data-testid="trade-panel-bound-outcome-banner"
            >
              {iconNode}
              <span className="font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
                Trading
              </span>
              <span className="font-semibold text-[var(--color-text)]">
                {boundOutcome.label}
              </span>
            </div>
          );
        })()}

      {/* Outcome cards — the primary action surface. Click selects a
          side; the highlighted card drives effectivePrice + CTA color.
          Reusable across BUY/SELL modes; SELL adds shares-held hint.
          `priceLoading` keeps the big-number area at "—" until the
          orderbook resolves so a fresh page load doesn't flash the
          last-trade anchor (e.g. NO=70¢) before flipping to the real
          best-quote (e.g. NO=8¢). Founder bug 2026-05-02.

          STORY B (2026-05-14) — when `boundOutcome` is provided without
          a real `noOutcomeId` (multi_outcome markets, where there is
          no complementary NO contract per the matcher's cross-book
          pair-mint guard at orders.service.ts:1377), only the YES card
          is rendered so the user can't tap into a shadow side the
          matcher won't fill. Otherwise the binary 2-column layout
          stays. */}
      <div
        className={`mb-3 grid gap-2 ${
          boundOutcome && !boundOutcome.noOutcomeId
            ? "grid-cols-1"
            : "grid-cols-2"
        }`}
      >
        <OutcomeCard
          mode={mode}
          outcome="yes"
          priceCents={yesPrice}
          impliedPriceCents={liveYesPrice}
          priceLoading={orderbook === null}
          selected={side === "yes"}
          onClick={() => selectOutcome("yes")}
          flashing={priceFlash === "yes"}
          sharesHeld={Math.max(
            0,
            (yesPosition ? Number(yesPosition.shares) : 0) -
              (reservedShares[yesOutcome.id] ?? 0),
          )}
        />
        {(!boundOutcome || boundOutcome.noOutcomeId) && (
          <OutcomeCard
            mode={mode}
            outcome="no"
            priceCents={noPrice}
            impliedPriceCents={liveNoPrice}
            priceLoading={orderbook === null}
            selected={side === "no"}
            onClick={() => selectOutcome("no")}
            flashing={priceFlash === "no"}
            sharesHeld={Math.max(
              0,
              (noPosition ? Number(noPosition.shares) : 0) -
                (reservedShares[noOutcome.id] ?? 0),
            )}
          />
        )}
      </div>

      {/* EPIC-053 STORY-2 (2026-05-08) — consolidated bid / ask / Δ row
          for the selected outcome. Mirrors Polymarket's "best bid · best
          ask · spread" row that sits between the outcome buttons and
          the order-entry block. Auto-hides when the orderbook isn't
          loaded or either side is null (one-sided book). The OutcomeCard
          shipped in #1574 carries the per-card spread already; this row
          unifies the answer to "what would I trade at right now" in a
          single line so the user doesn't have to mentally diff two
          OutcomeCards. */}
      {(() => {
        if (!orderbook) return null;
        const selectedBook = side === "yes" ? orderbook.yes : orderbook.no;
        const bid = selectedBook.bestBid;
        const ask = selectedBook.bestAsk;
        if (bid == null || ask == null) return null;
        const delta = ask - bid;
        // 2026-05-09 — guard against the "crossed book" case (bid >= ask).
        // This happens when both sides of the same outcome are held by the
        // same user (anti-self-trade filter prevents the matcher from
        // clearing them). The previous render emitted "-1¢" which read as
        // a UI bug. Now: detect the crossed condition + render
        // "Crossed" with a tooltip explaining the matcher posture.
        const isCrossed = delta <= 0;
        const isWide = !isCrossed && delta > wideSpreadThreshold;
        // 2026-05-10 — collapsed into <details> per the trade-panel
        // simplification pass. The bid/ask/spread row is diagnostic
        // info; OutcomeCards already show the actionable best price.
        // Wide-spread case stays summary-line so the user still sees
        // the warning at-a-glance even when the disclosure is closed.
        const wideSummary = isWide
          ? ` · ${delta}¢ wide`
          : isCrossed
            ? " · crossed"
            : "";
        return (
          <details
            className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] text-[11px] tabular-nums text-[var(--color-text2)]"
            data-testid="trade-panel-spread-row"
          >
            <summary className="flex cursor-pointer items-center justify-between px-3 py-2 text-[var(--color-text3)] hover:text-[var(--color-text2)]">
              <span>
                Order book details
                <span
                  className={
                    isWide
                      ? "ml-1 font-semibold text-[var(--color-no)]"
                      : "ml-1 text-[var(--color-text3)]"
                  }
                >
                  {wideSummary}
                </span>
              </span>
              <span aria-hidden className="text-[10px]">
                ▾
              </span>
            </summary>
            <div className="flex items-center justify-between border-t border-[var(--color-border)] px-3 py-2">
              <span>
                <span className="text-[var(--color-text3)]">Bid </span>
                <span className="font-semibold text-[var(--color-text)]">
                  {bid}¢
                </span>
              </span>
              <span>
                <span className="text-[var(--color-text3)]">Ask </span>
                <span className="font-semibold text-[var(--color-text)]">
                  {ask}¢
                </span>
              </span>
              <span>
                <span className="text-[var(--color-text3)]">Spread </span>
                {isCrossed ? (
                  <span
                    className="font-semibold uppercase tracking-wide text-[var(--color-text3)]"
                    title="Bid ≥ ask — both sides held by the same user (anti-self-trade prevents the matcher from clearing). Cancel one side or place an order from a different account to break the crossing."
                  >
                    Crossed
                  </span>
                ) : (
                  <span
                    className={`font-semibold tabular-nums ${
                      isWide
                        ? "text-[var(--color-no)]"
                        : "text-[var(--color-text)]"
                    }`}
                    title={
                      isWide
                        ? `Wide spread for ${market.categorySlug ?? "this category"} (threshold ${wideSpreadThreshold}¢). Liquidity is thin — your fill quality depends on how much spread you can absorb.`
                        : undefined
                    }
                  >
                    {delta}¢
                  </span>
                )}
              </span>
            </div>
          </details>
        );
      })()}

      {/* STORY-812 phase 3a (founder ask 2026-05-01) — asymmetric
          input. BUY = dollars (you have cash, not shares — share-
          count would force a mental conversion). SELL = shares
          (the natural unit of the position you're closing). The
          previous $/# toggle is gone — the unit follows the mode
          unambiguously. The amountMode state hook stays so the
          downstream math keeps working without a wider refactor;
          mode-toggle handlers above already pin amountMode to the
          right value, so the toggle UI was redundant. */}
      <div className="mb-2 flex items-center text-[10px] font-bold uppercase tracking-wide text-[var(--color-text3)]">
        <span>
          Amount{" "}
          {amountMode === "dollars" ? `(${displayCurrency})` : "(shares)"}
        </span>
      </div>
      <input
        type="number"
        min={0}
        // Founder bug 2026-05-02 (round 2) — the user wants BOTH:
        // typed decimals like 1.21 AND arrow keys that step by
        // whole dollars (5 → 6, not 5 → 5.01). Native HTML can't do
        // both with a single `step` attribute (it controls
        // validation AND arrow stepping together). So:
        //   - `step="any"` makes the browser validator permissive
        //     (any positive decimal types fine).
        //   - onKeyDown intercepts ArrowUp/ArrowDown and manually
        //     steps to the next/previous whole unit. From "5.01"
        //     ArrowUp lands on 6 (floor+1), ArrowDown lands on 5
        //     (ceil-1) — natural snap-to-grid behaviour.
        // 2026-05-11 founder report — share mode previously had step=1
        // which made fractional shares (e.g. 21.74) trigger the native
        // browser validator "two nearest valid values are 21 and 22"
        // popover. Post migration 0145 the minimum tradeable unit is
        // 1 centi-share = 0.01 actual shares, and the eToro % chips
        // compute fractional values (75% of 28.99 = 21.74). Switch
        // share mode to `step="any"` and let the matcher enforce the
        // centi-share floor server-side. Arrow-key snap stays
        // dollars-only since shares already accepts fractional input.
        step="any"
        inputMode="decimal"
        placeholder={amountMode === "dollars" ? "0.00" : "0"}
        value={amountInput}
        onChange={(e) => setAmountInput(e.target.value)}
        onKeyDown={(e) => {
          if (amountMode !== "dollars") return;
          if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
          e.preventDefault();
          const current = parseFloat(amountInput);
          const isInt = Number.isFinite(current) && Number.isInteger(current);
          const next =
            !Number.isFinite(current) || current < 0
              ? e.key === "ArrowUp"
                ? 1
                : 0
              : e.key === "ArrowUp"
                ? isInt
                  ? current + 1
                  : Math.floor(current) + 1
                : isInt
                  ? Math.max(0, current - 1)
                  : Math.ceil(current) - 1;
          setAmountInput(String(next));
        }}
        // 2026-05-11 — input value is fractional whole-shares (e.g.
        // "172.15"); availableShares is centi-shares (17215). Need to
        // divide by 100 so `max` actually matches the input's unit
        // and the browser validator rejects over-holding entries. The
        // submit-path also enforces server-side via the matcher's
        // SELL preflight; this max= just gives a client-side floor.
        max={
          mode === "sell" && amountMode === "shares"
            ? availableShares / 100
            : undefined
        }
        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2.5 font-mono text-[18px] font-bold tabular-nums text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
      />
      <div className="mt-1 flex items-center justify-between text-[10px] text-[var(--color-text3)]">
        <span>
          {mode === "buy"
            ? cashBalanceCents !== null
              ? `Virtual balance: ${fmtFx(cashBalanceCents)}`
              : "Virtual balance: —"
            : `You hold: ${fmtShares(availableShares)} ${side.toUpperCase()} shares`}
        </span>
        {amountMode === "dollars" && shares > 0 ? (
          <span className="font-mono">≈ {fmtShares(shares)} shares</span>
        ) : amountMode === "shares" && shares > 0 ? (
          <span className="font-mono">≈ {fmtFx(notionalCents)}</span>
        ) : null}
      </div>
      {/* 2026-05-10 — Round-up suggestion. Surfaces when the user's
          typed amount produces a sub-whole-centi residual (e.g. R$1 at
          26¢ → 76 centi-shares costing R$0,95, leaving 5¢ unspent).
          One click lands on the next display amount that perfectly
          consumes a whole centi. Region-agnostic — works for both
          BRL-primary and USDC-primary users. Skipped when residual = 0
          or round-up cost is disproportionate to the residual. */}
      {roundUpHint && (
        <button
          type="button"
          onClick={() => setAmountInput(roundUpHint.nextDisplayValue)}
          data-testid="trade-panel-round-up"
          className="mt-1 block w-full rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-left text-[10px] text-[var(--color-text2)] transition-colors hover:border-[var(--color-text2)] hover:text-[var(--color-text)]"
          title="Round up to consume the residual unspent amount in whole-contract increments"
        >
          Round up to{" "}
          <span className="font-semibold">
            {displayCurrency === "BRL" ? "R$" : "$"}
            {roundUpHint.nextDisplayValue}
          </span>{" "}
          → buys {roundUpHint.nextSharesLabel} contracts
        </button>
      )}
      {/* STORY-A — reservation disclosure on its own row beneath the
          legacy "Virtual balance" line, BUY mode only, only when the
          user actually has locked_orders > 0. Keeps the existing
          balance + "≈ N shares" indicator untouched (no layout
          regression) and adds the breakdown as a small clickable
          line that expands a list of the reserving orders. */}
      {mode === "buy" &&
        cashBalanceCents !== null &&
        lockedOrdersCents !== null &&
        lockedOrdersCents > 0 && (
          <button
            type="button"
            onClick={() => setReservedDisclosureOpen((v) => !v)}
            className="mt-0.5 block w-full text-left text-[10px] text-[var(--color-text3)] underline-offset-2 hover:text-[var(--color-text2)] hover:underline"
            aria-expanded={reservedDisclosureOpen}
            aria-controls="reserved-disclosure"
          >
            {fmtFx(totalBalanceCents ?? cashBalanceCents)} total ·{" "}
            {fmtFx(lockedOrdersCents)} reserved in {myReservingOrders.length}{" "}
            order
            {myReservingOrders.length === 1 ? "" : "s"}{" "}
            <span aria-hidden="true">{reservedDisclosureOpen ? "▾" : "▸"}</span>
          </button>
        )}
      {/* STORY-D (#1565) — "Max affordable" affordance. Surfaces the
          answer to "how many shares can I buy with my balance?" up
          front so the user doesn't have to click [Max] and read the
          input to find out. BUY mode only; recomputed live from
          cashBalanceCents + effectivePrice + takerFeeBps. Turns
          warning-color when the user's typed amount exceeds the
          affordable max. */}
      {mode === "buy" &&
        cashBalanceCents !== null &&
        cashBalanceCents > 0 &&
        effectivePrice > 0 &&
        (() => {
          const feeMultiplier = 1 + takerFeeBps / 10000;
          const perShareReservation = effectivePrice * feeMultiplier;
          if (perShareReservation <= 0) return null;
          const maxAffordableShares = Math.floor(
            cashBalanceCents / perShareReservation,
          );
          if (maxAffordableShares <= 0) return null;
          const maxNotionalUsdcCents = maxAffordableShares * effectivePrice;
          const exceedsAffordable = shares > maxAffordableShares;
          return (
            <div
              className={`mt-0.5 text-[10px] ${exceedsAffordable ? "font-medium text-[var(--color-no)]" : "text-[var(--color-text3)]"}`}
            >
              {exceedsAffordable
                ? `Exceeds your balance — max ${maxAffordableShares} shares (${fmtFx(maxNotionalUsdcCents)}) at ${effectivePrice}¢`
                : `Max affordable: ${maxAffordableShares} shares (${fmtFx(maxNotionalUsdcCents)}) at ${effectivePrice}¢`}
            </div>
          );
        })()}
      {mode === "buy" &&
        reservedDisclosureOpen &&
        myReservingOrders.length > 0 && (
          <div
            id="reserved-disclosure"
            className="mt-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-2 text-[11px]"
          >
            <div className="mb-1 flex items-baseline justify-between">
              <span className="text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
                Reserved across {myReservingOrders.length} open order
                {myReservingOrders.length === 1 ? "" : "s"}
              </span>
              <a
                href="/portfolio"
                className="text-[10px] text-[var(--color-brand)] hover:underline"
              >
                Manage →
              </a>
            </div>
            <ul className="divide-y divide-[var(--color-border)]">
              {myReservingOrders.slice(0, 8).map((o) => (
                <li
                  key={o.id}
                  className="flex items-center justify-between gap-2 py-1"
                >
                  <span className="truncate text-[var(--color-text2)]">
                    <span className="font-mono">{o.priceCents}¢</span>
                    {" × "}
                    <span className="font-mono">{o.remainingShares}</span>{" "}
                    {o.side.toUpperCase()}{" "}
                    <a
                      href={`/markets/${o.marketSlug}`}
                      className="text-[var(--color-text3)] hover:text-[var(--color-text)]"
                    >
                      {o.marketSlug}
                    </a>
                  </span>
                  <span className="font-mono">{fmtFx(o.reservedCents)}</span>
                </li>
              ))}
              {myReservingOrders.length > 8 && (
                <li className="py-1 text-center text-[10px] text-[var(--color-text3)]">
                  +{myReservingOrders.length - 8} more — see /portfolio
                </li>
              )}
            </ul>
          </div>
        )}

      {/* Quick chips. 2026-05-11 — SELL mode with a held position
          switches to eToro-style percentage chips (25/50/75/100% of
          available shares); founder direction unifies the close-
          position flow with the TradePanel sell flow ("why can't it
          be like this adding only the partial percentage partial
          close orders like etoro"). Other modes keep the prior
          dollar/share-count chips with Max. */}
      <div className="mt-2 mb-3 grid grid-cols-4 gap-1.5">
        {showPercentChips
          ? percentChips.map((p) => {
              const centiAtPct = Math.floor((availableShares * p) / 100);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => applyPercentChip(p)}
                  className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] py-1.5 text-[11px] font-semibold transition-colors hover:border-[var(--color-text2)] hover:text-[var(--color-text)]"
                  data-testid={`sell-pct-chip-${p}`}
                >
                  <div>{p}%</div>
                  <div className="text-[9px] font-normal text-[var(--color-text3)]">
                    {Number.isInteger(centiAtPct / 100)
                      ? centiAtPct / 100
                      : (centiAtPct / 100).toFixed(2)}{" "}
                    sh
                  </div>
                </button>
              );
            })
          : (amountMode === "dollars" ? dollarChips : shareChips).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => applyChip(v)}
                className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] py-1.5 text-[11px] font-semibold transition-colors hover:border-[var(--color-text2)] hover:text-[var(--color-text)]"
              >
                {amountMode === "dollars"
                  ? displayCurrency === "BRL"
                    ? `R$${v}`
                    : `$${v}`
                  : v}
              </button>
            ))}
        {!showPercentChips && (
          <button
            type="button"
            onClick={() => (mode === "buy" ? applyMaxBuy() : applyMaxSell())}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] py-1.5 text-[11px] font-semibold transition-colors hover:border-[var(--color-text2)] hover:text-[var(--color-text)]"
          >
            Max
          </button>
        )}
      </div>

      {/* STORY-818 (2026-05-01) — slippage UI as max price.
          Replaces the old "Limit price" label + "% implied"
          caption with founder-direction framing: "MAX PRICE" leads,
          the relationship to the side-aware best quote is the
          secondary detail (e.g. "2¢ above best ask"), and the
          probability percent is gone from the default view (the
          founder rationale: cents = probability points anyway, so
          the percent is redundant on a 0-100¢ scale and breaks at
          extremes — 95¢ + 7.1% > $1.00). Tooltip on the label
          explains the cents-as-probability-points framing for
          users who want education. */}
      <label className="mb-3 block">
        <span className="mb-1 flex items-baseline justify-between text-[10px] font-bold uppercase tracking-wide text-[var(--color-text3)]">
          {/* CSS-only popover — native title= had a ~1.5s hover
                delay so users hovered, saw nothing, gave up. The
                grouped span shows the description immediately on
                hover or keyboard focus. */}
          {/* Founder ask 2026-05-02 — fold the 3-line "Highest
                price you'll pay" helper into the same hover popover
                so the casual surface stays a clean label + input
                pair. Pattern matches the You-pay tooltip. */}
          <span className="group relative inline-flex items-center gap-1">
            <span
              tabIndex={0}
              className="cursor-help underline decoration-dotted underline-offset-2 outline-none"
              aria-describedby="limit-price-tooltip"
            >
              Limit price
            </span>
            <span aria-hidden="true" className="text-[10px] opacity-70">
              ⓘ
            </span>
            <span
              id="limit-price-tooltip"
              role="tooltip"
              className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-max max-w-[280px] rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-[10px] font-normal normal-case leading-tight tracking-normal text-[var(--color-text2)] shadow-lg group-hover:block group-focus-within:block"
            >
              {mode === "buy"
                ? "Highest price you'll pay per share. Order fills immediately if the book has shares at this price or lower; otherwise it rests as a limit order on the book. Cents = probability points (50¢ = 50% implied)."
                : "Lowest price you'll accept per share. Order fills immediately if the book has buyers at this price or higher; otherwise it rests as a limit order on the book. Cents = probability points (50¢ = 50% implied)."}
            </span>
          </span>
          <span className="font-normal normal-case text-[var(--color-text3)] tabular-nums">
            {(() => {
              const delta = limitPrice - sidePrice;
              const cmp =
                mode === "buy"
                  ? delta > 0
                    ? `${delta}¢ above best ask`
                    : delta < 0
                      ? `${Math.abs(delta)}¢ below best ask`
                      : "at best ask"
                  : delta < 0
                    ? `${Math.abs(delta)}¢ below best bid`
                    : delta > 0
                      ? `${delta}¢ above best bid`
                      : "at best bid";
              return cmp;
            })()}
          </span>
        </span>
        <div className="flex items-stretch gap-2">
          <input
            type="number"
            min={1}
            max={99}
            step={1}
            inputMode="numeric"
            placeholder={String(sidePrice)}
            value={limitPriceInput}
            onChange={(e) => setLimitPriceInput(e.target.value)}
            // Founder ask 2026-05-01 — arrow up/down on an empty
            // limit-price field used to jump to min=1 (browser
            // default), confusing because the placeholder shows the
            // best quote (e.g. 30). Pre-populate with sidePrice so
            // the first arrow press steps from the live quote
            // (30 → 31 / 30 → 29) instead of from 0/1. Only fires
            // when the field is empty AND it's an arrow key — typing
            // "31" still works exactly the same.
            onKeyDown={(e) => {
              if (
                limitPriceInput === "" &&
                (e.key === "ArrowUp" || e.key === "ArrowDown")
              ) {
                setLimitPriceInput(String(sidePrice));
              }
            }}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 font-mono text-[14px] tabular-nums text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
            aria-describedby="limit-price-tooltip"
          />
          {/* Founder ask 2026-05-09 — explicit "use best bid/ask"
              affordance. The placeholder + arrow-key autofill gave
              users the value implicitly; this exposes it as a
              tappable suggestion so they don't have to read the
              orderbook separately to find the live quote. Disabled
              when the limit input already matches sidePrice (no
              effect to perform); shows side-aware label so the
              user knows whether they're filling from best ask
              (BUY) or best bid (SELL). */}
          <button
            type="button"
            onClick={() => setLimitPriceInput(String(sidePrice))}
            disabled={limitPrice === sidePrice}
            data-testid="trade-panel-use-best"
            title={
              mode === "buy"
                ? "Fill at the current best ask"
                : "Fill at the current best bid"
            }
            className="shrink-0 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 py-2 text-[10px] font-bold uppercase tracking-wide text-[var(--color-text2)] transition-colors hover:border-[var(--color-text2)] hover:text-[var(--color-text)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {mode === "buy" ? `Best ${sidePrice}¢` : `Best ${sidePrice}¢`}
          </button>
        </div>
      </label>

      {/* STORY-709 max-slippage block deleted in STORY-812 phase 3d:
          orderType is permanently 'limit' so the slippage details
          disclosure (only rendered for market orders) was dead. The
          limit-price input above already caps acceptable fills. The
          backend payload still passes through any value the user
          types via API, so power users can still set slippage via
          their own client until STORY-817 surfaces an explicit
          advanced-toggle UI. */}

      {/* STORY-821 + 2026-05-02 polish — progressive disclosure with
          the supporting math folded into the You-pay tooltip rather
          than a parallel "Details" disclosure. Average price + shares
          are already visible elsewhere (the outcome card price + the
          "≈ N shares" caption under the amount input), so duplicating
          them here added noise without information. The default view
          now drops to three lines (You pay / Max payout / Potential
          profit). Worst-case disclosure for market BUY still surfaces
          inline when it diverges meaningfully from the estimate. */}
      {shares > 0 && (
        <div className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-[11px]">
          {mode === "buy" ? (
            <>
              {/* 2026-05-09 — Polymarket-style summary. Founder bug:
                  $1 input rendering as "You pay $0.98" because the
                  whole-share floor leaves a few cents unspent. The
                  industry-standard framing leads with the payout (the
                  good thing the user is buying) and reduces the cost
                  to small print, not a headline. Cost remains accurate
                  in the tooltip + the Confirm button label. */}
              <div className="flex items-baseline justify-between">
                <span
                  className="text-[var(--color-text3)] cursor-help"
                  title={`${(takerFeeBps / 100).toFixed(2)}% taker fee (${fmtFx(feeCents)}) included. Average price ${effectivePrice}¢ × ${fmtShares(shares)} shares = ${fmtFx(notionalCents)}; total ${fmtFx(estimatedCostCents)}.`}
                >
                  {t("tradePanel.payoutIfYes", "Payout if won")}
                </span>
                <span
                  className="font-mono text-[18px] font-bold tabular-nums"
                  style={{ color: "var(--color-yes)" }}
                >
                  {fmtFx(maxPayoutCents)}
                </span>
              </div>
              <div className="mt-0.5 text-[10px] text-[var(--color-text3)]">
                {t("tradePanel.buyingContracts", {
                  shares: fmtShares(shares),
                  price: effectivePrice,
                  defaultValue: `Buying ${fmtShares(shares)} contracts at ${effectivePrice}¢ each`,
                })}
                {feeCents > 0 ? ` · ${fmtFx(feeCents)} fee` : ""}
              </div>
              {/* EPIC-029 BR Story 3 — when displayCurrencyPrimary='BRL'
                  surface the underlying USDC value so the user can
                  verify the platform spread. Hidden when primary is
                  already USDC. */}
              {displayCurrencyPrimary === "BRL" &&
                displayCurrency === "BRL" &&
                usdcPerBrl !== null && (
                  <div
                    className="mt-0.5 flex items-baseline justify-end text-[10px] tabular-nums text-[var(--color-text3)]"
                    data-testid="trade-panel-usdc-reference"
                  >
                    {(() => {
                      const dual = formatMoneyPrimary(
                        Math.round(maxPayoutCents / usdcPerBrl),
                        "USDC",
                        usdcPerBrl,
                      );
                      return <span>≈ {dual.primary} USDC</span>;
                    })()}
                  </div>
                )}
              <div className="mt-1.5 border-t border-[var(--color-border)] pt-1.5">
                <SummaryRow
                  label={t("tradePanel.potentialProfit")}
                  value={
                    profitCents >= 0
                      ? `+${fmtFx(profitCents)} (+${profitPct.toFixed(0)}%)`
                      : `−${fmtFx(Math.abs(profitCents))}`
                  }
                  tone={profitCents >= 0 ? "positive" : "negative"}
                />
              </div>
            </>
          ) : (
            <SummaryRow
              label={t("tradePanel.youReceive")}
              value={fmtFx(netProceedsCents)}
              tone="positive"
              bold
              tooltip={`Net of ${(takerFeeBps / 100).toFixed(2)}% trading fee (${fmtFx(feeCents)}). Average price ${effectivePrice}¢ × ${fmtShares(shares)} shares = ${fmtFx(notionalCents)}; minus fee ${fmtFx(feeCents)}; net ${fmtFx(netProceedsCents)}.`}
            />
          )}
        </div>
      )}

      {/* The legacy `noLiquidityHint` block was gated on
          `orderType === 'market'`, which has been permanently false
          since STORY-812 phase 2 pinned the panel to limit-only with
          smart routing. The fill-plan preview below already gives
          *conditional* liquidity feedback — it only renders the
          "rests as limit" notice when the user's chosen size
          actually exceeds book depth (Option B per founder direction
          2026-05-02), which is the intended UX. Dead block deleted. */}

      {/* STORY-812 phase 3b — fill plan preview. Always-on (when
          orderbook is loaded + shares > 0) so the user confirms
          the smart-routing decision before submit:
            - case='full'    → all shares fill immediately at avg ≤ price
            - case='partial' → split: X fill immediately, Y rests
            - case='zero'    → no immediate fill; whole order rests
          The matcher actually does this routing; the preview just
          mirrors it. Tone uses positive (green) for full fills,
          neutral (info-blue) for splits, caution (amber) for full-
          rest. Replaces the prior caution-toned warning panel that
          only fired on partial/zero. */}
      {preflight &&
        shares > 0 &&
        (() => {
          const fillable = preflight.fillableShares;
          const resting = preflight.requestedShares - fillable;
          const tone =
            preflight.case === "full"
              ? "positive"
              : preflight.case === "partial"
                ? "info"
                : "caution";
          const toneVar =
            tone === "positive"
              ? "var(--color-yes)"
              : tone === "info"
                ? "var(--color-status-info, var(--color-brand))"
                : "var(--color-status-caution)";
          return (
            <div
              role="status"
              aria-live="polite"
              className="mb-2 rounded-md border px-2.5 py-1.5 text-[11px]"
              style={{
                borderColor: `color-mix(in srgb, ${toneVar} 50%, transparent)`,
                background: `color-mix(in srgb, ${toneVar} 8%, transparent)`,
                color: toneVar,
              }}
            >
              <p className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.4px]">
                Fill plan
              </p>
              {fillable > 0 && (
                <p>
                  ✓ <strong>{fmtShares(fillable)}</strong> share
                  {fillable === 100 ? "" : "s"} fill immediately at avg{" "}
                  <strong>{preflight.avgFillPriceCents}¢</strong>
                </p>
              )}
              {resting > 0 && (
                <p>
                  ○ <strong>{fmtShares(resting)}</strong> share
                  {resting === 100 ? "" : "s"} rest as a limit at{" "}
                  <strong>{effectivePrice}¢</strong>
                  {preflight.case === "zero" ? (
                    <>
                      {" "}
                      — no offers at or {mode === "buy"
                        ? "below"
                        : "above"}{" "}
                      your price right now
                    </>
                  ) : null}
                </p>
              )}
            </div>
          );
        })()}

      {insufficientBalance &&
        (() => {
          // 2026-05-09 founder direction — replace the alarm-style
          // "insufficient balance" message with a calm, action-first
          // suggestion: "with your $X you can buy ~N shares (final
          // $Y including spread + fees)" + a one-tap CTA that fills
          // the amount input to that exact amount. The user expects
          // "what CAN I buy" not "what you can't afford."
          const feeMultiplier = 1 + takerFeeBps / 10000;
          const perShareCostCents = effectivePrice * feeMultiplier;
          const affordableShares =
            cashBalanceCents !== null && perShareCostCents > 0
              ? Math.floor(cashBalanceCents / perShareCostCents)
              : 0;
          const affordableNotionalCents = Math.floor(
            affordableShares * effectivePrice,
          );
          const affordableFeeCents = Math.floor(
            (affordableNotionalCents * takerFeeBps) / 10000,
          );
          const affordableTotalCents =
            affordableNotionalCents + affordableFeeCents;
          return (
            <div
              role="status"
              className="mb-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              style={{
                borderColor:
                  "color-mix(in srgb, var(--color-brand) 40%, transparent)",
                background:
                  "color-mix(in srgb, var(--color-brand) 8%, transparent)",
                color: "var(--color-status-info)",
              }}
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 16 16"
                className="mt-[2px] h-3.5 w-3.5 flex-shrink-0"
                fill="currentColor"
              >
                <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm-.75 4h1.5v1.5h-1.5V4zm0 3h1.5v5h-1.5V7z" />
              </svg>
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className="font-semibold">
                  {t("tradePanel.affordableSuggestionTitle", {
                    defaultValue: "Adjust to what your balance covers",
                  })}
                </span>
                {affordableShares > 0 ? (
                  <span className="opacity-90">
                    {t("tradePanel.affordableSuggestionBody", {
                      defaultValue:
                        "With {{have}} you can buy ~{{shares}} shares at {{price}}¢ — total {{total}} (incl. {{feePct}}% fee).",
                      have: fmtFx(Number(cashBalanceCents)),
                      shares: affordableShares,
                      price: effectivePrice,
                      total: fmtFx(affordableTotalCents),
                      feePct: (takerFeeBps / 100).toFixed(
                        takerFeeBps % 100 === 0 ? 0 : 2,
                      ),
                    })}
                  </span>
                ) : (
                  <span className="opacity-90">
                    {t("tradePanel.affordableSuggestionEmpty", {
                      defaultValue:
                        "Your {{have}} balance can't cover a single share at {{price}}¢. Add funds or pick a market with a lower price.",
                      have: fmtFx(Number(cashBalanceCents)),
                      price: effectivePrice,
                    })}
                  </span>
                )}
                {affordableShares > 0 && (
                  <button
                    type="button"
                    onClick={applyMaxBuy}
                    className="self-start rounded-md border border-[var(--color-brand)] px-2 py-1 text-[11px] font-bold transition-colors hover:bg-[var(--color-brand)] hover:text-white"
                    style={{ color: "var(--color-brand)" }}
                  >
                    {t("tradePanel.buyMaxAvailable", {
                      defaultValue: "Use my full balance",
                    })}
                  </button>
                )}
              </div>
            </div>
          );
        })()}

      {error && !insufficientBalance && (
        <div
          role="alert"
          className="mb-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
          style={{
            borderColor:
              "color-mix(in srgb, var(--color-status-negative) 50%, transparent)",
            background:
              "color-mix(in srgb, var(--color-status-negative) 8%, transparent)",
            color: "var(--color-status-negative)",
          }}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 16 16"
            className="mt-[2px] h-3.5 w-3.5 flex-shrink-0"
            fill="currentColor"
          >
            <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm-.75 4h1.5l-.2 5h-1.1l-.2-5zM8 12.5a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div
          role="status"
          className="mb-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
          style={
            success.tone === "warning"
              ? {
                  borderColor:
                    "color-mix(in srgb, var(--color-status-caution) 50%, transparent)",
                  background:
                    "color-mix(in srgb, var(--color-status-caution) 8%, transparent)",
                  color: "var(--color-status-caution)",
                }
              : {
                  borderColor:
                    "color-mix(in srgb, var(--color-yes) 50%, transparent)",
                  background:
                    "color-mix(in srgb, var(--color-yes) 8%, transparent)",
                  color: "#86efac",
                }
          }
        >
          {success.tone === "warning" ? (
            <AlertTriangle
              size={14}
              strokeWidth={1.75}
              aria-hidden="true"
              className="flex-shrink-0"
            />
          ) : (
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              className="mt-[2px] h-3.5 w-3.5 flex-shrink-0"
              fill="currentColor"
            >
              <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm3.78 5.97l-4.5 4.5a.75.75 0 01-1.06 0l-2-2a.75.75 0 011.06-1.06l1.47 1.47 3.97-3.97a.75.75 0 011.06 1.06z" />
            </svg>
          )}
          <div className="flex flex-col gap-1">
            <span
              className="font-semibold"
              style={{
                color: success.tone === "warning" ? "#fde68a" : "#a7f3c2",
              }}
            >
              {success.title}
            </span>
            <span className="text-[10.5px] opacity-90">{success.detail}</span>
            {/* EPIC-047 — "Add commentary" affordance. Opens the
                unified composer with the market pre-referenced via
                $slug so the existing posts.attached_market_id parser
                resolves the embed. Always shown on a successful
                trade so the user can post their take immediately. */}
            {success.tone === "success" ? (
              <ComposerButton
                ariaLabel="Add commentary about this trade"
                className="self-start rounded border px-2 py-0.5 text-[10px] font-bold transition-colors hover:opacity-80"
                initialDestination={{ kind: "profile", anonymous: false }}
                initialBody={`Just placed a ${side.toUpperCase()} order on $${market.slug}. `}
                initialSentiment={side === "yes" ? "bullish" : "bearish"}
              >
                + Add commentary
              </ComposerButton>
            ) : null}
            {/* STORY-812 phase 3c — "Cancel pending" affordance.
                Renders only when the placed order has a resting
                remainder we can cancel. Clicking calls cancelOrder
                via the existing STORY-306 surface; the toast then
                updates with the cancellation outcome. */}
            {success.pendingOrderId && success.pendingShares ? (
              <button
                type="button"
                onClick={async () => {
                  const id = success.pendingOrderId!;
                  try {
                    await api.cancelOrder(id);
                    setSuccess({
                      title: "Pending portion cancelled",
                      detail: `Released the reservation for ${success.pendingShares} ${side.toUpperCase()} at ${effectivePrice}¢.`,
                      tone: "success",
                    });
                    window.dispatchEvent(
                      new CustomEvent("granbay.balance.changed"),
                    );
                  } catch (err) {
                    setSuccess({
                      title: "Couldn't cancel",
                      detail:
                        err instanceof Error
                          ? err.message
                          : "Try /home → Active orders.",
                      tone: "warning",
                    });
                  }
                }}
                className="self-start rounded border px-2 py-0.5 text-[10px] font-bold transition-colors hover:opacity-80"
                style={{
                  borderColor:
                    "color-mix(in srgb, currentColor 40%, transparent)",
                  color: "inherit",
                }}
              >
                Cancel pending ({success.pendingShares})
              </button>
            ) : null}
          </div>
        </div>
      )}

      {/* EPIC-041 part 3d — per-trade auto-share toggle. Default
          checked; un-checking makes THIS trade private without
          mutating the global pref (set in /settings/privacy). */}
      <label className="mb-2 flex items-center gap-2 text-[11px] text-[var(--color-text2)] select-none">
        <input
          type="checkbox"
          checked={shareToFeed}
          onChange={(e) => setShareToFeed(e.target.checked)}
          disabled={submitting}
          className="h-3.5 w-3.5 cursor-pointer accent-[var(--color-brand)] disabled:cursor-not-allowed"
          aria-label={t("tradePanel.shareToFeedAria")}
        />
        <span>{t("tradePanel.shareToFeedLabel")}</span>
      </label>

      <button
        type="submit"
        disabled={
          submitting ||
          shares <= 0 ||
          (mode === "sell" && shares > availableShares) ||
          insufficientBalance
        }
        className="w-full rounded-lg py-2.5 text-[13px] font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        style={{ background: ctaBg }}
      >
        <span className="inline-flex items-center justify-center gap-2">
          {submitting && (
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              className="h-3.5 w-3.5 animate-spin"
              fill="none"
            >
              <circle
                cx="8"
                cy="8"
                r="6"
                stroke="currentColor"
                strokeOpacity="0.3"
                strokeWidth="2"
              />
              <path
                d="M14 8a6 6 0 00-6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          )}
          <span>{ctaLabel}</span>
        </span>
      </button>

      {/* "Limit orders rest on the book until they match" footer
          deleted 2026-05-02 — post-STORY-812 the panel only does
          limits so the line was redundant. The fill-plan preview
          above already tells the user exactly what their specific
          order will do ("367 shares fill immediately at avg 30¢ ·
          49 shares rest as a limit at 30¢") which is way more
          informative than a static line. */}

      {confirming && (
        <ConfirmHighValueOrderModal
          mode={mode}
          side={side}
          shares={shares}
          effectivePrice={effectivePrice}
          feeCents={feeCents}
          takerFeeBps={takerFeeBps}
          reservationCents={reservationCents}
          netProceedsCents={netProceedsCents}
          maxPayoutCents={maxPayoutCents}
          profitCents={profitCents}
          submitting={submitting}
          onCancel={() => setConfirming(false)}
          onConfirm={async () => {
            setConfirming(false);
            await executePlaceOrder();
          }}
        />
      )}
    </form>
  );
}

// ─── STORY-346 pre-flight liquidity check ─────────────────────

export type PreflightCase = "zero" | "partial" | "full";

export interface PreflightResult {
  case: PreflightCase;
  fillableShares: number;
  requestedShares: number;
  // Volume-weighted average price across the levels that WOULD
  // fill (cents). null when fillableShares = 0 (no levels to
  // average across).
  avgFillPriceCents: number | null;
  // Best resting price on the OTHER side of the order — what a
  // user would set as a Limit price to participate in the book.
  // For BUY taker → first ask; for SELL taker → first bid.
  // null when the entire side is empty.
  bestOpposingPriceCents: number | null;
  // Same-side best resting price (where the user would rest a
  // limit at parity). null when no orders rest on that side.
  bestSameSidePriceCents: number | null;
}

interface PreflightInput {
  orderbook: OrderbookResponse;
  side: Mode;
  outcome: Outcome;
  requestedShares: number;
  fullFillThresholdPct: number;
  // STORY-812 phase 3b — limit-price cap. When set, the walk
  // skips ladder levels outside the band: BUY only consumes
  // asks ≤ this price; SELL only consumes bids ≥ this price.
  // Mirrors the matcher's price-improvement contract so the
  // preview reflects what would actually fill at submit time.
  // When null/undefined the walk has no cap (legacy market-
  // order behavior).
  maxAcceptablePriceCents?: number | null;
}

/**
 * Walks the relevant side of the orderbook (asks for BUY, bids
 * for SELL) and accumulates levels until the requested share
 * count is met. Returns the case classification + math the
 * warning UI needs to render.
 *
 * Exported for direct unit testing — cleaner than mounting the
 * whole panel for chip-math style tests.
 */
export function computePreflightFill({
  orderbook,
  side,
  outcome,
  requestedShares,
  fullFillThresholdPct,
  maxAcceptablePriceCents,
}: PreflightInput): PreflightResult {
  const sideBook = outcome === "yes" ? orderbook.yes : orderbook.no;
  // BUY taker eats ASKS (cheapest first). SELL taker eats BIDS
  // (highest first). Both already sorted by the API per the
  // OrderbookService contract.
  const ladder = side === "buy" ? sideBook.asks : sideBook.bids;
  const opposingLadder = side === "buy" ? sideBook.bids : sideBook.asks;

  let remaining = requestedShares;
  let weightedSum = 0;
  let filled = 0;
  for (const level of ladder) {
    if (remaining <= 0) break;
    // STORY-812 phase 3b — respect the limit-price cap. Once we hit
    // a level outside the band the matcher won't cross either, so
    // the preview must stop too (the unfilled remainder rests as a
    // GTC limit at the user's price; STORY-811 changed market-FAK
    // to cancel-remainder, so for FAK orders the unfilled portion
    // simply won't fill).
    if (maxAcceptablePriceCents != null) {
      if (side === "buy" && level.priceCents > maxAcceptablePriceCents) break;
      if (side === "sell" && level.priceCents < maxAcceptablePriceCents) break;
    }
    const available = Number(level.sizeShares);
    if (available <= 0) continue;
    const take = Math.min(remaining, available);
    weightedSum += take * level.priceCents;
    filled += take;
    remaining -= take;
  }

  const fillablePct =
    requestedShares > 0 ? (filled / requestedShares) * 100 : 0;
  const caseClass: PreflightCase =
    filled === 0
      ? "zero"
      : fillablePct >= fullFillThresholdPct
        ? "full"
        : "partial";

  return {
    case: caseClass,
    fillableShares: filled,
    requestedShares,
    avgFillPriceCents: filled > 0 ? Math.round(weightedSum / filled) : null,
    bestOpposingPriceCents:
      opposingLadder.length > 0 ? opposingLadder[0].priceCents : null,
    bestSameSidePriceCents: ladder.length > 0 ? ladder[0].priceCents : null,
  };
}

/**
 * STORY-307d — high-value-order confirmation modal.
 *
 * Renders a portal-style fixed overlay (no createPortal — `position:
 * fixed` already escapes the form's stacking context). Repeats the
 * order summary so the user can sanity-check before committing real
 * money. Esc + backdrop click + Cancel all dismiss to the panel
 * unchanged; Confirm submits.
 */
function ConfirmHighValueOrderModal(props: {
  mode: Mode;
  side: Outcome;
  shares: number;
  effectivePrice: number;
  feeCents: number;
  takerFeeBps: number;
  reservationCents: number;
  netProceedsCents: number;
  maxPayoutCents: number;
  profitCents: number;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const {
    mode,
    side,
    shares,
    effectivePrice,
    feeCents,
    takerFeeBps,
    reservationCents,
    netProceedsCents,
    maxPayoutCents,
    profitCents,
    submitting,
    onCancel,
    onConfirm,
  } = props;
  const displayCurrency = useDisplayCurrency();
  const fxBrl = useFxRate("BRL");
  const usdcPerBrl = fxBrl?.rate ?? null;
  // 2026-05-09 — USDC-as-truth refactor. Centralised in
  // formatUsdcCentsForDisplay so the inline pattern only lives in one
  // place across the trade-panel + receipts + portfolio surfaces.
  const fmtFx = (usdcCents: number): string =>
    formatUsdcCentsForDisplay(usdcCents, displayCurrency, usdcPerBrl);

  const verb =
    mode === "buy"
      ? t("tradePanel.confirmModalVerbBuy")
      : t("tradePanel.confirmModalVerbSell");
  const totalCents = mode === "buy" ? reservationCents : netProceedsCents;
  const ctaBg =
    mode === "sell"
      ? "var(--color-no)"
      : side === "yes"
        ? "var(--color-yes)"
        : "var(--color-no)";

  if (typeof document === "undefined") return null;
  // Portal to <body> — same containing-block fix as #1387/#1388/#1389.
  // SiteHeader's backdrop-blur-md re-roots `position: fixed` to its
  // own box, so without a Portal the high-value-order confirm modal
  // anchors off-center on desktop / off-canvas on mobile.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-order-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      <button
        type="button"
        aria-label={t("tradePanel.confirmModalDismissAria")}
        onClick={onCancel}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative max-h-[calc(100dvh-2rem)] w-full max-w-sm overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-xl">
        <h2
          id="confirm-order-title"
          className="text-[13px] font-bold uppercase tracking-wide text-[var(--color-text)]"
        >
          Confirm {verb.toLowerCase()} order
        </h2>
        <p className="mt-1 text-[11px] text-[var(--color-text3)]">
          Large order — please double-check before placing.
        </p>

        <dl className="mt-3 space-y-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2.5 text-[12px]">
          <div className="flex items-baseline justify-between">
            <dt className="text-[var(--color-text3)]">Outcome</dt>
            <dd
              className="font-bold tabular-nums"
              style={{
                color: side === "yes" ? "var(--color-yes)" : "var(--color-no)",
              }}
            >
              {side.toUpperCase()}
            </dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-[var(--color-text3)]">Shares</dt>
            <dd className="font-bold tabular-nums">
              {Number.isInteger(shares / 100)
                ? String(shares / 100)
                : (shares / 100).toFixed(2)}
            </dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-[var(--color-text3)]">Limit price</dt>
            <dd className="font-bold tabular-nums">{effectivePrice}¢</dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-[var(--color-text3)]">
              Fees ({(takerFeeBps / 100).toFixed(2)}%)
            </dt>
            <dd className="tabular-nums">{fmtFx(feeCents)}</dd>
          </div>
          <div className="flex items-baseline justify-between border-t border-[var(--color-border)] pt-1.5">
            <dt className="text-[var(--color-text3)]">
              {mode === "buy" ? "You pay" : "Net proceeds"}
            </dt>
            <dd className="font-bold tabular-nums">{fmtFx(totalCents)}</dd>
          </div>
          {mode === "buy" && (
            <>
              <div className="flex items-baseline justify-between">
                <dt className="text-[var(--color-text3)]">Max payout</dt>
                <dd className="tabular-nums">{fmtFx(maxPayoutCents)}</dd>
              </div>
              <div className="flex items-baseline justify-between">
                <dt className="text-[var(--color-text3)]">Potential profit</dt>
                <dd
                  className="font-bold tabular-nums"
                  style={{ color: "var(--color-yes)" }}
                >
                  +{fmtFx(profitCents)}
                </dd>
              </div>
            </>
          )}
        </dl>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="min-h-[44px] flex-1 rounded-lg border border-[var(--color-border)] py-2 text-[12px] font-bold uppercase tracking-wide text-[var(--color-text2)] transition-colors hover:border-[var(--color-text2)] md:min-h-0"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              void onConfirm();
            }}
            disabled={submitting}
            className="min-h-[44px] flex-1 rounded-lg py-2 text-[12px] font-bold uppercase tracking-wide text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 md:min-h-0"
            style={{ background: ctaBg }}
          >
            {submitting ? "Placing…" : `Confirm · ${fmtFx(totalCents)}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// OutcomeCard + SummaryRow extracted to `components/trade/` 2026-05-02
// — pure presentational, imported above. No behaviour change.

// STORY-709 slippage helpers (slippageBandColor / Label / Tooltip)
// removed in STORY-812 phase 3d 2026-05-02: their only caller was
// the max-slippage <details> block, which itself only rendered for
// `orderType === 'market'` — permanently false post-phase-2. The
// matcher still respects an explicit `maxSlippageCents` value via
// the API, so power users aren't blocked; STORY-817 (advanced
// order types toggle) will surface a UI for it again if/when
// needed.
